import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Typography,
  Table,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  message,
  notification,
  Alert,
  Divider,
  Tabs,
  Badge,
  Tooltip,
  List,
  Avatar,
  Menu,
  Checkbox,
  Dropdown,
  Popconfirm,
  AutoComplete,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UploadOutlined,
  BarcodeOutlined,
  QrcodeOutlined,
  AlertOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  FileTextOutlined,
  TagsOutlined,
  AppstoreOutlined,
  ReloadOutlined,
  MailOutlined,
  BellOutlined,
  CopyOutlined,
  ShoppingOutlined,
  NumberOutlined,
  TrophyOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  UpOutlined,
  DownOutlined,
  FileExcelOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import ProductBulkUploadModal from '../components/ProductBulkUploadModal';
import apiService from '../../../services/apiService';
import { useAuthStore } from '../../../stores/authStore';
import { useTranslation } from "react-i18next";
import * as XLSX from 'xlsx';
import { buildProductRows, buildBundleRows, buildAlternateRows, PRODUCT_HEADERS, BUNDLE_HEADERS, ALTERNATE_HEADERS } from '../utils/productExport';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

// 批次控制策略枚舉
enum BatchControlType {
  FIFO = 'fifo',
  LIFO = 'lifo', 
  FEFO = 'fefo',
  LEFO = 'lefo',
  RANDOM = 'random',
  LARGEST_QTY_FIRST = 'largest_qty_first',
  SMALLEST_QTY_FIRST = 'smallest_qty_first',
  BATCH_NUMBER_ASC = 'batch_number_asc',
  BATCH_NUMBER_DESC = 'batch_number_desc'
}

// 批次控制策略配置
interface BatchControlConfig {
  type: BatchControlType;
  label: string;
  description: string;
  explanation: string;
  useCases: string[];
  algorithm: (stockLots: StockLot[]) => StockLot[];
}

// 庫存批次接口
interface StockLot {
  id: string;
  productId: string;
  lotNumber: string;
  serialNumber?: string;
  quantity: number;
  entryDate: Date;
  expiryDate?: Date;
  location: string;
  batchNumber?: string;
}

// 產品接口定義
interface ProductMaster {
  id: string;
  merchantReference: string; // 商家參考
  sku: string; // SKU
  alternateSkuList: string[]; // 備用SKU
  internalReference: string; // 內部參考（系統生成）
  barcode: string; // 條形碼
  productName: string; // 產品名稱
  productNameEnglish?: string; // 英文產品名稱
  productNameChinese?: string; // 中文產品名稱
  description?: string; // 描述
  pickingRemark?: string; // 揀貨備註（列印於揀貨單產品名稱下方）
  brand?: string; // 品牌
  storeCodes?: string[]; // Store codes (多選)

  // 國際運輸/海關資料
  hsCode?: string; // 海關編碼 (HS Code)
  origin?: string; // 原產地
  declaredValue?: number; // 申報價值
  // customerId?: string; // ❌ Deprecated: 改用 customerIds 多选
  customerIds?: string[]; // ✅ Customer IDs (for non-TVMALL products, 多選)
  customerNames?: string; // 客戶名稱（用於顯示）
  
  // 產品本身尺寸和重量
  length?: number; // 產品長度 (cm)
  width?: number; // 產品寬度 (cm)
  height?: number; // 產品高度 (cm)
  weight?: number; // 產品重量 (kg)
  
  // 包裝尺寸和重量（用於CBM計算）
  packagingLength?: number; // 包裝長度 (cm)
  packagingWidth?: number; // 包裝寬度 (cm)
  packagingHeight?: number; // 包裝高度 (cm)
  packagingWeight?: number; // 包裝重量 (kg)
  packageCBM?: number; // 包裝體積 (自動計算)
  unitCBM?: number; // 單位體積 (自動計算)
  
  // 溫度要求
  temperatureRequirement?: string; // 溫度要求
  
  // 單位和轉換
  baseUOM: string; // 基本單位
  packagingUOM: string; // 包裝單位
  conversionFactor: number; // 轉換因子
  
  // 批次和序號
  lotNumber?: string; // 批次號
  serialNumber?: string; // 序號
  
  // 批次控制設定
  batchControlType: BatchControlType; // 批次控制策略
  lotNumberRequired: boolean; // 是否需要批次號
  serialNumberRequired: boolean; // 是否需要序列號
  
  // 庫存管理
  maxStock: number; // 最大庫存
  totalStock?: number; // 總庫存（從 inventory 表計算）

  // 銷售渠道配比
  salesChannelRatios?: { channel: string; ratio: number }[];
  
  // 產品捆綁
  isBundle: boolean; // 是否為捆綁產品
  bundleItems?: BundleItem[]; // 捆綁項目

  // 備用 SKU 子產品標識（如果此產品的 itemCode 被註冊為另一產品的備用 SKU）
  isChildSku?: boolean; // 是否為子 SKU
  parentSkuInfo?: { parentProductId: string; parentItemCode: string; parentItemName: string }; // 父 SKU 資訊

  // 多商店支持（後端邏輯，前端無需設置）
  multiStoreEnabled?: boolean; // 多商店支持（後端自動處理）
  storeMappings?: StoreMapping[]; // 商店映射（後端管理）
  
  // 狀態
  isActive: boolean; // 是否活躍
  createdAt: Date; // 創建時間
  updatedAt: Date; // 更新時間
  
  // 警報設置
  alertEmails: string[]; // 警報郵件列表
  alertEnabled: boolean; // 警報啟用
  
  // 產品屬性標籤
  productAttributes?: string[]; // 產品屬性標籤數組
  
  // 供應商和分類
  supplier?: string; // 供應商（來自採購模組的供應商主數據）
  category?: string[]; // 產品分類（用戶可創建的標籤）
}

interface BundleItem {
  id: string;
  productId: string;
  productName: string;
  quantity: number;
  sku: string;
  baseUOM?: string; // 基本單位
}

interface StoreMapping {
  id: string;
  storeName: string;
  storeCode: string;
  sku: string;
  barcode: string;
}

interface KPIStats {
  totalProducts: number;
  totalValue: number;
  lowStockItems: number;
  outOfStockItems: number;
}

const ProductMasterSetup: React.FC = () => {
    const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams()
  const initialSkuParam = searchParams.get('sku') || ''
  
  // 獲取當前用戶信息
  const { user } = useAuthStore();
  const isCustomerRole = user?.role === 'CUSTOMER';

  // 狀態管理
  const [products, setProducts] = useState<ProductMaster[]>([]);
  // 全量產品快取：用於套裝下拉，不受當前 SKU 過濾影響
  const [allProductsCache, setAllProductsCache] = useState<ProductMaster[]>([]);
  // 套裝產品搜尋結果（支援伺服器端搜尋）
  const [bundleSearchResults, setBundleSearchResults] = useState<ProductMaster[]>([]);
  const [bundleSearchLoading, setBundleSearchLoading] = useState<boolean>(false);
  const bundleSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // 由現有產品彙總的屬性選項（去重）+ 本地快取擴充
  const defaultAttributeSeeds = [
    '紅色', '藍色', '黑色', '白色',
    'S', 'M', 'L', 'XL',
    '64GB', '128GB', '256GB',
    '小號', '中號', '大號',
    '有線', '無線'
  ];

  const attributeOptions = useMemo(() => {
    const set = new Set<string>();
    products.forEach(p => {
      (p.productAttributes || []).forEach(attr => {
        if (attr && typeof attr === 'string') set.add(attr);
      });
    });
    try {
      const saved = JSON.parse(localStorage.getItem('product-master-attribute-options') || '[]');
      if (Array.isArray(saved)) saved.forEach((a: any) => { if (a && typeof a === 'string') set.add(a); });
    } catch {}
    // 若目前尚無任何彙總資料，提供一組常用預設選項
    if (set.size === 0) {
      defaultAttributeSeeds.forEach(s => set.add(s));
    }
    return Array.from(set).map(v => ({ value: v, label: v }));
  }, [products]);

  const handleAttributesChange = (values: any) => {
    try {
      const prev = JSON.parse(localStorage.getItem('product-master-attribute-options') || '[]');
      const set = new Set<string>(Array.isArray(prev) ? prev : []);
      (Array.isArray(values) ? values : []).forEach((v: any) => { if (v && typeof v === 'string') set.add(v); });
      localStorage.setItem('product-master-attribute-options', JSON.stringify(Array.from(set)));
    } catch {}
  };
  const [loading, setLoading] = useState(false);
  const lastDetailRef = useRef<any>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [editingProduct, setEditingProduct] = useState<ProductMaster | null>(null);
  const [isReadOnly, setIsReadOnly] = useState<boolean>(false);
  const [form] = Form.useForm();
  // 條形碼列表：單行輸入或掃描後追加在此，提交時會合併到 hidden 欄位 barcode
  const [barcodeList, setBarcodeList] = useState<string[]>([]);
  // 備用SKU列表：單行輸入或掃描後追加在此，提交時會合併到 hidden 欄位 alternateSku
  const [alternateSkuList, setAlternateSkuList] = useState<string[]>([]);
  // Store codes state
  const [availableStoreCodes, setAvailableStoreCodes] = useState<string[]>([]);
  // Customers state
  const [availableCustomers, setAvailableCustomers] = useState<Array<{id: string; name: string}>>([]);

  const normalizeBarcodes = (list: string[]) => Array.from(new Set(list.map(s => String(s).trim()).filter(Boolean)));
  const syncBarcodeField = (list: string[]) => {
    form.setFieldsValue({ barcode: (list || []).join(',') });
  };
  const addBarcode = (code?: string) => {
    const value = String(code ?? form.getFieldValue('barcodeInput') ?? '').trim();
    if (!value) return;
    const next = normalizeBarcodes([...(barcodeList || []), value]);
    setBarcodeList(next);
    form.setFieldsValue({ barcodeInput: '' });
    syncBarcodeField(next);
    message.success(t('已添加條形碼'));
  };
  const removeBarcode = (code: string) => {
    const next = (barcodeList || []).filter(b => b !== code);
    setBarcodeList(next);
    syncBarcodeField(next);
  };
  // 產品套裝開關本地狀態，保證UI即時更新
  const [bundleEnabled, setBundleEnabled] = useState<boolean>(false);
  // 監聽是否啟用產品套裝（模態內表單）
  const isBundleEnabled = (Form as any).useWatch ? (Form as any).useWatch('isBundle', form) : undefined;
  // 監聽是否啟用散件（無外包裝）模式
  const isPackagedEnabled = (Form as any).useWatch ? (Form as any).useWatch('isPackaged', form) : undefined;
  // 監聽SKU ID值
  const skuValue = (Form as any).useWatch ? (Form as any).useWatch('sku', form) : undefined;
  // 編輯中產品的子 SKU 狀態（用於禁用備用 SKU 功能）
  const [editingIsChildSku, setEditingIsChildSku] = useState<boolean>(false);
  const [editingParentSkuInfo, setEditingParentSkuInfo] = useState<{ parentProductId: string; parentItemCode: string; parentItemName: string } | null>(null);
  // 備用 SKU 下拉選單搜尋結果
  const [alternateSkuSearchResults, setAlternateSkuSearchResults] = useState<Array<{id: string; itemCode: string; itemName: string}>>([]);

  // 產品表格排序狀態
  const [productSortField, setProductSortField] = useState<string>('');
  const [productSortOrder, setProductSortOrder] = useState<'asc' | 'desc' | ''>('');
  // 分頁控制（改為受控，讓500一頁生效）
  const [productCurrentPage, setProductCurrentPage] = useState<number>(1);
  const [productPageSize, setProductPageSize] = useState<number>(20);
  const [productTotalCount, setProductTotalCount] = useState<number>(0);
  // 銷售渠道清單（可改為從後端載入）
  // 產品搜尋過濾 (SKU/Barcode/Name) - Initialize with URL parameter if present
  const [productSearchFilter, setProductSearchFilter] = useState<string>(initialSkuParam);
  // 產品搜尋自動完成選項
  const [productSearchOptions, setProductSearchOptions] = useState<{ value: string; label: string; sku: string; barcode: string }[]>([]);
  // 客戶過濾 (用於按客戶篩選產品)
  const [selectedCustomerName, setSelectedCustomerName] = useState<string>('');
  const [customersWithProducts, setCustomersWithProducts] = useState<Array<{id: string | null; name: string}>>([]);
  const salesChannelOptions = useMemo(() => ['HKTVmall', 'Shopify', 'Amazon', 'eBay', 'Taobao'], []);

  // Clear URL parameter after initial load
  useEffect(() => {
    if (initialSkuParam) {
      setSearchParams({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 產品列設置下拉菜單可見狀態
  const [productColumnSettingsVisible, setProductColumnSettingsVisible] = useState(false);

  // 產品表格列配置狀態
  const [productColumnSettings, setProductColumnSettings] = useState(() => {
    const saved = localStorage.getItem('product-master-columns-config');
    const version = localStorage.getItem('product-master-columns-version');
    
    // 檢查版本，如果不是最新版本則重置配置（2025 拆分列版本）
    if (version !== '2025-01-v3' || !saved) {
      const defaultConfig = {
        // 拆分後的細分列（默認可見且按順序排列）
        productName: { visible: true, order: 0 },
        sku: { visible: true, order: 1 },
        barcode: { visible: true, order: 2 },
        merchantReference: { visible: true, order: 3 },
        internalReference: { visible: true, order: 4 },
        totalStock: { visible: true, order: 5 },
        size: { visible: true, order: 6 },
        cbm: { visible: true, order: 7 },
        baseUOM: { visible: true, order: 8 },
        packagingUOM: { visible: true, order: 9 },
        // 其餘已有列
        supplier: { visible: true, order: 10 },
        category: { visible: true, order: 11 },
        customer: { visible: true, order: 12 },
        batchControl: { visible: true, order: 13 },
        status: { visible: true, order: 14 },
        actions: { visible: true, order: 15 },
        // 舊的聚合列，默認隱藏
        product: { visible: false, order: 100 },
        references: { visible: false, order: 101 },
        stock: { visible: false, order: 102 },
        dimensions: { visible: false, order: 103 },
        units: { visible: false, order: 104 }
      } as any;
      localStorage.setItem('product-master-columns-config', JSON.stringify(defaultConfig));
      localStorage.setItem('product-master-columns-version', '2025-01-v3');
      return defaultConfig;
    }
    
    try {
      return JSON.parse(saved);
    } catch {
      const defaultConfig = {
        productName: { visible: true, order: 0 },
        sku: { visible: true, order: 1 },
        barcode: { visible: true, order: 2 },
        merchantReference: { visible: true, order: 3 },
        internalReference: { visible: true, order: 4 },
        totalStock: { visible: true, order: 5 },
        size: { visible: true, order: 6 },
        cbm: { visible: true, order: 7 },
        baseUOM: { visible: true, order: 8 },
        packagingUOM: { visible: true, order: 9 },
        supplier: { visible: true, order: 10 },
        category: { visible: true, order: 11 },
        customer: { visible: true, order: 12 },
        batchControl: { visible: true, order: 13 },
        status: { visible: true, order: 14 },
        actions: { visible: true, order: 15 },
        product: { visible: false, order: 100 },
        references: { visible: false, order: 101 },
        stock: { visible: false, order: 102 },
        dimensions: { visible: false, order: 103 },
        units: { visible: false, order: 104 }
      } as any;
      return defaultConfig;
    }
  });

  // 處理列排序
  const handleProductColumnSort = (columnKey: string) => {
    // 僅在後端排序生效：循環 asc -> desc
    if (productSortField === columnKey) {
      setProductSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setProductSortField(columnKey);
      setProductSortOrder('asc');
    }
  };

  // 獲取排序圖標
  const getProductSortIcon = (columnKey: string) => {
    if (productSortField !== columnKey) return null;
    return productSortOrder === 'asc' ? 
      <SortAscendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} /> :
      <SortDescendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} />;
  };

  const [kpiStats, setKpiStats] = useState<KPIStats>({
    totalProducts: 0,
    totalValue: 0,
    lowStockItems: 0,
    outOfStockItems: 0,
  });

  // 折疊控制（頁面頂部 KPI 區域 + 操作欄位）
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const toggleCollapsed = () => setCollapsed(prev => !prev);

  // 批次控制策略：由後端 batch_strategies 動態載入（若無資料時會由 useEffect 觸發 seed）
  const batchControlConfigs: BatchControlConfig[] = (Array.isArray((window as any).__dynamicBatchConfigs) && (window as any).__dynamicBatchConfigs.length
    ? (window as any).__dynamicBatchConfigs
    : []);

  // 批次控制策略執行函數
  const executeBatchControl = (productId: string, requestedQuantity: number): StockLot[] => {
    const product = products.find(p => p.id === productId);
    if (!product) return [];

    // 模擬獲取該產品的所有庫存批次
    const stockLots = getMockStockLots(productId);
    
    // 獲取批次控制配置
    const sourceConfigs: any[] = (Array.isArray((window as any).__dynamicBatchConfigs) && (window as any).__dynamicBatchConfigs.length
      ? (window as any).__dynamicBatchConfigs
      : batchControlConfigs);
    const config = sourceConfigs.find(c => c.type === product.batchControlType);
    if (!config) return stockLots;

    // 應用批次控制算法
    const sortedLots = config.algorithm(stockLots);
    
    // 根據請求數量選擇批次
    const selectedLots: StockLot[] = [];
    let remainingQuantity = requestedQuantity;
    
    for (const lot of sortedLots) {
      if (remainingQuantity <= 0) break;
      
      const quantityToTake = Math.min(lot.quantity, remainingQuantity);
      selectedLots.push({
        ...lot,
        quantity: quantityToTake
      });
      
      remainingQuantity -= quantityToTake;
    }
    
    return selectedLots;
  };

  // 模擬庫存批次數據
  const getMockStockLots = (productId: string): StockLot[] => {
    return [
      {
        id: `${productId}-lot-1`,
        productId,
        lotNumber: 'LOT-2024-001',
        quantity: 50,
        entryDate: new Date('2024-01-01'),
        expiryDate: new Date('2024-12-31'),
        location: 'A-01-01',
        batchNumber: 'BATCH-001'
      },
      {
        id: `${productId}-lot-2`,
        productId,
        lotNumber: 'LOT-2024-002',
        quantity: 75,
        entryDate: new Date('2024-01-15'),
        expiryDate: new Date('2024-11-30'),
        location: 'A-01-02',
        batchNumber: 'BATCH-002'
      },
      {
        id: `${productId}-lot-3`,
        productId,
        lotNumber: 'LOT-2024-003',
        quantity: 30,
        entryDate: new Date('2024-02-01'),
        expiryDate: new Date('2024-10-31'),
        location: 'A-01-03',
        batchNumber: 'BATCH-003'
      },
      {
        id: `${productId}-lot-4`,
        productId,
        lotNumber: 'LOT-2024-004',
        quantity: 100,
        entryDate: new Date('2024-02-15'),
        expiryDate: new Date('2025-01-31'),
        location: 'A-01-04',
        batchNumber: 'BATCH-004'
      }
    ];
  };

  // 批次控制演示函數
  const showBatchControlDemo = (config: BatchControlConfig) => {
    // 創建演示用的庫存批次數據
    const demoStockLots: StockLot[] = [
      {
        id: 'demo-1',
        productId: 'demo',
        lotNumber: 'LOT-001',
        quantity: 50,
        entryDate: new Date('2024-01-01'),
        expiryDate: new Date('2024-12-31'),
        location: 'A-01-01',
        batchNumber: 'BATCH-001'
      },
      {
        id: 'demo-2',
        productId: 'demo',
        lotNumber: 'LOT-002',
        quantity: 75,
        entryDate: new Date('2024-01-15'),
        expiryDate: new Date('2024-11-30'),
        location: 'A-01-02',
        batchNumber: 'BATCH-002'
      },
      {
        id: 'demo-3',
        productId: 'demo',
        lotNumber: 'LOT-003',
        quantity: 30,
        entryDate: new Date('2024-02-01'),
        expiryDate: new Date('2024-10-31'),
        location: 'A-01-03',
        batchNumber: 'BATCH-003'
      },
      {
        id: 'demo-4',
        productId: 'demo',
        lotNumber: 'LOT-004',
        quantity: 100,
        entryDate: new Date('2024-02-15'),
        expiryDate: new Date('2025-01-31'),
        location: 'A-01-04',
        batchNumber: 'BATCH-004'
      }
    ];

    // 應用批次控制算法
    const sortedLots = config.algorithm([...demoStockLots]);

    // 創建演示表格列
    const demoColumns = [
      {
        title: t('排序'),
        dataIndex: 'index',
        key: 'index',
        width: 60,
        render: (_: any, __: any, index: number) => (
          <Badge count={index + 1} style={{ backgroundColor: '#52c41a' }} />
        )
      },
      {
        title: t('批次號'),
        dataIndex: 'lotNumber',
        key: 'lotNumber',
      },
      {
        title: t('數量'),
        dataIndex: 'quantity',
        key: 'quantity',
        render: (quantity: number) => `${quantity} PCS`
      },
      {
        title: t('入庫日期'),
        dataIndex: 'entryDate',
        key: 'entryDate',
        render: (date: Date) => date.toLocaleDateString('zh-TW')
      },
      {
        title: t('到期日期'),
        dataIndex: 'expiryDate',
        key: 'expiryDate',
        render: (date: Date) => date ? date.toLocaleDateString('zh-TW') : '-'
      },
      {
        title: t('位置'),
        dataIndex: 'location',
        key: 'location',
      }
    ];

    Modal.info({
      title: t('{{label}} - 排序演示', { label: config.label }),
      width: 800,
      content: (
        <div>
          <Alert
            message={t('策略說明')}
            description={config.explanation}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          
          <Title level={5}>{t('出庫優先順序：')}</Title>
          <Table
            dataSource={sortedLots}
            columns={demoColumns}
            pagination={false}
            size="small"
            rowKey="id"
          />
          
          <Alert
            message={t('說明')}
            description={t('表格中的順序即為該策略下的出庫優先順序，編號1的批次將優先出庫。')}
            type="success"
            showIcon
            style={{ marginTop: 16 }}
          />
        </div>
      ),
    });
  };

  // 測試產品的批次控制
  const testBatchControl = (product: ProductMaster) => {
    const sourceConfigs2: any[] = (Array.isArray((window as any).__dynamicBatchConfigs) && (window as any).__dynamicBatchConfigs.length
      ? (window as any).__dynamicBatchConfigs
      : batchControlConfigs);
    const config = sourceConfigs2.find(c => c.type === product.batchControlType);
    if (!config) {
      message.error(t('未找到批次控制配置'));
      return;
    }

    const stockLots = getMockStockLots(product.id);
    const sortedLots = config.algorithm([...stockLots]);

    // 模擬一次出庫請求
    const requestQuantity = 120; // 模擬請求120個單位
    const selectedLots = executeBatchControl(product.id, requestQuantity);

    Modal.info({
      title: t('{{productName}} - 批次控制測試', { productName: product.productName }),
      width: 900,
      content: (
        <div>
          <Alert
            message={t('當前策略：{{label}}', { label: config.label })}
            description={config.explanation}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Row gutter={16}>
            <Col span={12}>
              <Title level={5}>{t('所有庫存批次：')}</Title>
              <Table
                dataSource={stockLots}
                columns={[
                  {
                    title: t('批次號'),
                    dataIndex: 'lotNumber',
                    key: 'lotNumber',
                  },
                  {
                    title: t('數量'),
                    dataIndex: 'quantity',
                    key: 'quantity',
                    render: (qty: number) => `${qty} ${product.baseUOM}`
                  },
                  {
                    title: t('入庫日期'),
                    dataIndex: 'entryDate',
                    key: 'entryDate',
                    render: (date: Date) => date.toLocaleDateString('zh-TW')
                  },
                  {
                    title: t('到期日期'),
                    dataIndex: 'expiryDate',
                    key: 'expiryDate',
                    render: (date?: Date) => date ? date.toLocaleDateString('zh-TW') : '-'
                  }
                ]}
                pagination={false}
                size="small"
                rowKey="id"
              />
            </Col>

            <Col span={12}>
              <Title level={5}>{t('模擬出庫（需求：{{qty}} {{uom}}）：', { qty: requestQuantity, uom: product.baseUOM })}</Title>
              <Table
                dataSource={selectedLots}
                columns={[
                  {
                    title: t('優先級'),
                    dataIndex: 'index',
                    key: 'index',
                    width: 60,
                    render: (_: any, __: any, index: number) => (
                      <Badge count={index + 1} style={{ backgroundColor: '#52c41a' }} />
                    )
                  },
                  {
                    title: t('批次號'),
                    dataIndex: 'lotNumber',
                    key: 'lotNumber',
                  },
                  {
                    title: t('出庫數量'),
                    dataIndex: 'quantity',
                    key: 'quantity',
                    render: (qty: number) => (
                      <Text strong style={{ color: '#f5222d' }}>
                        {qty} {product.baseUOM}
                      </Text>
                    )
                  },
                  {
                    title: t('位置'),
                    dataIndex: 'location',
                    key: 'location',
                  }
                ]}
                pagination={false}
                size="small"
                rowKey="id"
              />
              
              <Alert
                message={t('出庫結果')}
                description={t('總計出庫：{{qty}} {{uom}}', { qty: selectedLots.reduce((sum, lot) => sum + lot.quantity, 0), uom: product.baseUOM })}
                type="success"
                showIcon
                style={{ marginTop: 8 }}
              />
            </Col>
          </Row>
        </div>
      ),
    });
  };

  // Mock數據
  const mockProducts: ProductMaster[] = [
    {
      id: '1',
      merchantReference: 'MERCH-001',
      sku: 'SKU-001',
      internalReference: 'INT-2024-001',
      barcode: '1234567890123',
      productName: '高級無線耳機',
      productNameEnglish: 'Premium Wireless Headphones',
      productNameChinese: '高級無線耳機',
      description: t('高品質無線藍牙耳機，支持降噪功能'),
      brand: 'AudioTech',
      length: 20,
      width: 15,
      height: 8,
      weight: 0.3,
      packageCBM: 0.0024,
      unitCBM: 0.0000024,
      baseUOM: 'PCS',
      packagingUOM: 'BOX',
      conversionFactor: 1,
      lotNumber: 'LOT-2024-001',
      serialNumber: 'SN-2024-001',
      batchControlType: BatchControlType.FIFO,
      lotNumberRequired: true,
      serialNumberRequired: true,
      maxStock: 500,
      totalStock: 150,
      isBundle: false,
      multiStoreEnabled: true,
      isActive: true,
      createdAt: new Date('2024-01-01'),
      updatedAt: new Date('2024-01-15'),
      alertEmails: ['admin@company.com', 'warehouse@company.com'],
      alertEnabled: true,
      productAttributes: ['黑色', '無線', '降噪', '高級'],
      supplier: 'Tech Electronics Co.', // 供應商
      category: ['電子產品', '音響設備', '消費級'], // 產品分類
    },
    {
      id: '2',
      merchantReference: 'MERCH-002',
      sku: 'SKU-002',
      internalReference: 'INT-2024-002',
      barcode: '9876543210987',
      productName: 'iPhone 15 Pro',
      productNameEnglish: 'iPhone 15 Pro',
      productNameChinese: 'iPhone 15 Pro',
      description: t('Apple iPhone 15 Pro，支持5G網絡，256GB存儲'),
      brand: 'Apple',
      length: 15,
      width: 8,
      height: 1,
      weight: 0.2,
      packageCBM: 0.00012,
      unitCBM: 0.00000012,
      baseUOM: 'PCS',
      packagingUOM: 'BOX',
      conversionFactor: 1,
      lotNumber: 'LOT-2024-002',
      serialNumber: 'SN-2024-002',
      batchControlType: BatchControlType.FEFO,
      lotNumberRequired: true,
      serialNumberRequired: false,
      maxStock: 200,
      totalStock: 2,
      isBundle: false,
      multiStoreEnabled: true,
      isActive: true,
      createdAt: new Date('2024-01-02'),
      updatedAt: new Date('2024-01-16'),
      alertEmails: ['admin@company.com'],
      alertEnabled: true,
      productAttributes: ['5G', '智能', '高性能'],
      supplier: 'Mobile Devices Ltd.', // 供應商
      category: ['電子產品', '通訊設備', '智能手機'], // 產品分類
    },
    {
      id: '3',
      merchantReference: 'MERCH-003',
      sku: 'SKU-003',
      internalReference: 'INT-2024-003',
      barcode: '4567891230456',
      productName: '辦公套裝',
      productNameEnglish: 'Office Bundle',
      productNameChinese: '辦公套裝',
      description: t('包含筆記本、鋼筆、文件夾的辦公套裝'),
      brand: 'OfficePro',
      length: 30,
      width: 25,
      height: 5,
      weight: 0.8,
      packageCBM: 0.00375,
      unitCBM: 0.00000375,
      baseUOM: 'SET',
      packagingUOM: 'BOX',
      conversionFactor: 1,
      lotNumber: 'LOT-2024-003',
      serialNumber: 'SN-2024-003',
      batchControlType: BatchControlType.LARGEST_QTY_FIRST,
      lotNumberRequired: false,
      serialNumberRequired: false,
      maxStock: 100,
      totalStock: 0,
      isBundle: true,
      bundleItems: [
        {
          id: 'bundle-1',
          productId: '1',
          productName: '筆記本',
          quantity: 1,
          sku: 'SKU-001',
        },
        {
          id: 'bundle-2',
          productId: '2',
          productName: '鋼筆',
          quantity: 2,
          sku: 'SKU-002',
        },
      ],
      multiStoreEnabled: true,
      isActive: true,
      createdAt: new Date('2024-01-03'),
      updatedAt: new Date('2024-01-17'),
      alertEmails: ['admin@company.com'],
      alertEnabled: true,
      productAttributes: ['辦公用品', '套裝', '商務'],
      supplier: 'Office Supplies Inc.', // 供應商
      category: ['辦公用品', '文具套裝', '商務用品'], // 產品分類
    },
    {
      id: '4',
      merchantReference: 'MERCH-004',
      sku: 'SKU-004',
      internalReference: 'INT-2024-004',
      barcode: '7890123456789',
      productName: 'iPhone 15 Pro Case',
      productNameEnglish: 'iPhone 15 Pro Case',
      productNameChinese: 'iPhone 15 Pro 保護殼',
      description: t('Apple iPhone 15 Pro 專用保護殼，透明硅膠材質'),
      brand: 'Apple',
      length: 16,
      width: 9,
      height: 2,
      weight: 0.05,
      packageCBM: 0.000288,
      unitCBM: 0.0000000288,
      baseUOM: 'PCS',
      packagingUOM: 'BOX',
      conversionFactor: 10,
      lotNumber: 'LOT-2024-004',
      serialNumber: 'SN-2024-004',
      batchControlType: BatchControlType.FIFO,
      lotNumberRequired: false,
      serialNumberRequired: false,
      maxStock: 500,
      totalStock: 200,
      isBundle: false,
      multiStoreEnabled: true,
      isActive: true,
      createdAt: new Date('2024-01-01'),
      updatedAt: new Date('2024-01-15'),
      alertEmails: ['admin@company.com'],
      alertEnabled: true,
      productAttributes: ['透明', '硅膠', '保護', 'iPhone'],
      supplier: 'Mobile Accessories Co.',
      category: ['電子產品', '手機配件', '保護殼'],
    },
    {
      id: '5',
      merchantReference: 'MERCH-005',
      sku: 'SKU-005',
      internalReference: 'INT-2024-005',
      barcode: '5678901234567',
      productName: '無線充電器',
      productNameEnglish: 'Wireless Charger',
      productNameChinese: '無線充電器',
      description: t('MagSafe 兼容無線充電器，15W快速充電'),
      brand: 'ChargeTech',
      length: 10,
      width: 10,
      height: 1,
      weight: 0.15,
      packageCBM: 0.0001,
      unitCBM: 0.000001,
      baseUOM: 'PCS',
      packagingUOM: 'BOX',
      conversionFactor: 5,
      lotNumber: 'LOT-2024-005',
      serialNumber: 'SN-2024-005',
      batchControlType: BatchControlType.FEFO,
      lotNumberRequired: false,
      serialNumberRequired: true,
      maxStock: 200,
      totalStock: 75,
      isBundle: false,
      multiStoreEnabled: true,
      isActive: true,
      createdAt: new Date('2024-01-01'),
      updatedAt: new Date('2024-01-15'),
      alertEmails: ['admin@company.com'],
      alertEnabled: true,
      productAttributes: ['無線', '快充', 'MagSafe', '15W'],
      supplier: 'Tech Accessories Ltd.',
      category: ['電子產品', '充電設備', '無線充電'],
    },
    {
      id: '6',
      merchantReference: 'MERCH-006',
      sku: 'SKU-006',
      internalReference: 'INT-2024-006',
      barcode: '3456789012345',
      productName: 'USB-C 數據線',
      productNameEnglish: 'USB-C Cable',
      productNameChinese: 'USB-C 數據線',
      description: t('高品質USB-C數據線，支持快速充電和數據傳輸'),
      brand: 'CableTech',
      length: 2,
      width: 1,
      height: 1,
      weight: 0.05,
      packageCBM: 0.000002,
      unitCBM: 0.000000002,
      baseUOM: 'PCS',
      packagingUOM: 'BOX',
      conversionFactor: 10,
      lotNumber: 'LOT-2024-006',
      serialNumber: 'SN-2024-006',
      batchControlType: BatchControlType.FIFO,
      lotNumberRequired: false,
      serialNumberRequired: false,
      maxStock: 1000,
      totalStock: 500,
      isBundle: false,
      multiStoreEnabled: true,
      isActive: true,
      createdAt: new Date('2024-01-01'),
      updatedAt: new Date('2024-01-15'),
      alertEmails: ['admin@company.com'],
      alertEnabled: true,
      productAttributes: ['USB-C', '快充', '數據傳輸'],
      supplier: 'Tech Accessories Ltd.',
      category: ['電子產品', '數據線', '充電配件'],
    },
    {
      id: '7',
      merchantReference: 'MERCH-007',
      sku: 'SKU-007',
      internalReference: 'INT-2024-007',
      barcode: '6789012345678',
      productName: '藍牙滑鼠',
      productNameEnglish: 'Bluetooth Mouse',
      productNameChinese: '藍牙滑鼠',
      description: t('人體工學藍牙滑鼠，2.4GHz無線連接'),
      brand: 'MouseTech',
      length: 12,
      width: 6,
      height: 4,
      weight: 0.08,
      packageCBM: 0.000288,
      unitCBM: 0.0000000288,
      baseUOM: 'PCS',
      packagingUOM: 'BOX',
      conversionFactor: 12,
      lotNumber: 'LOT-2024-007',
      serialNumber: 'SN-2024-007',
      batchControlType: BatchControlType.FIFO,
      lotNumberRequired: false,
      serialNumberRequired: true,
      maxStock: 200,
      totalStock: 80,
      isBundle: false,
      multiStoreEnabled: true,
      isActive: true,
      createdAt: new Date('2024-01-01'),
      updatedAt: new Date('2024-01-15'),
      alertEmails: ['admin@company.com'],
      alertEnabled: true,
      productAttributes: ['藍牙', '無線', '人體工學'],
      supplier: 'Tech Electronics Co.',
      category: ['電子產品', '電腦配件', '滑鼠'],
    },
    {
      id: '8',
      merchantReference: 'MERCH-008',
      sku: 'SKU-008',
      internalReference: 'INT-2024-008',
      barcode: '9012345678901',
      productName: '筆記本電腦包',
      productNameEnglish: 'Laptop Bag',
      productNameChinese: '筆記本電腦包',
      description: t('防水筆記本電腦包，適合15.6寸筆記本電腦'),
      brand: 'BagPro',
      length: 40,
      width: 30,
      height: 8,
      weight: 0.6,
      packageCBM: 0.0096,
      unitCBM: 0.0000096,
      baseUOM: 'PCS',
      packagingUOM: 'BOX',
      conversionFactor: 6,
      lotNumber: 'LOT-2024-008',
      serialNumber: 'SN-2024-008',
      batchControlType: BatchControlType.FIFO,
      lotNumberRequired: false,
      serialNumberRequired: false,
      maxStock: 100,
      totalStock: 25,
      isBundle: false,
      multiStoreEnabled: true,
      isActive: true,
      createdAt: new Date('2024-01-01'),
      updatedAt: new Date('2024-01-15'),
      alertEmails: ['admin@company.com'],
      alertEnabled: true,
      productAttributes: ['防水', '15.6寸', '便攜'],
      supplier: 'Fashion Wholesale Group',
      category: ['配件', '電腦包', '商務用品'],
    },
  ];

  // Mock 供應商數據（來自採購模組）
  const mockSuppliers = [
    { value: 'Tech Electronics Co.', label: t('Tech Electronics Co.') },
    { value: 'Mobile Devices Ltd.', label: t('Mobile Devices Ltd.') },
    { value: 'Office Supplies Inc.', label: t('Office Supplies Inc.') },
    { value: 'Industrial Materials Corp.', label: t('Industrial Materials Corp.') },
    { value: 'Fashion Wholesale Group', label: t('Fashion Wholesale Group') },
    { value: 'Food & Beverage Suppliers', label: t('Food & Beverage Suppliers') },
    { value: 'Automotive Parts Ltd.', label: t('Automotive Parts Ltd.') },
    { value: 'Medical Equipment Supply', label: t('Medical Equipment Supply') },
  ];

  // Mock 產品分類數據
  const mockCategories = [
    '電子產品', '音響設備', '消費級', '通訊設備', '智能手機',
    '辦公用品', '文具套裝', '商務用品', '工業用品', '原材料',
    '服裝配飾', '時尚用品', '食品飲料', '生鮮產品', '汽車配件',
    '機械零件', '醫療設備', '保健用品', '家居用品', '裝飾材料'
  ];

  // 加載產品數據
  const loadAvailableStoreCodes = async () => {
    try {
      const resp: any = await apiService.getAvailableStoreCodes();
      const codes = resp?.storeCodes || [];
      setAvailableStoreCodes(codes);
    } catch (error) {
      console.error('Failed to load available store codes:', error);
    }
  };

  const loadAvailableCustomers = async () => {
    try {
      const resp: any = await apiService.getCustomers({ page: 1, limit: 1000 });
      const customers = resp?.customers || [];
      setAvailableCustomers(customers.map((c: any) => ({ id: c.id, name: c.name })));
    } catch (error) {
      console.error('Failed to load available customers:', error);
    }
  };

  // 載入有產品關聯的客戶 (用於產品列表過濾下拉)
  const loadCustomersWithProducts = async () => {
    try {
      const resp: any = await apiService.getCustomersWithInventory();
      // API returns { success: true, data: [...] }
      if (resp?.success && resp?.data) {
        setCustomersWithProducts(resp.data);
      }
    } catch (error) {
      console.error('Failed to load customers with products:', error);
    }
  };

  const loadProducts = async (page: number = productCurrentPage, limit: number = productPageSize, searchOverride?: string, customerNameOverride?: string) => {
    setLoading(true);
    try {
      const resp: any = await apiService.getProductsV2({
        page,
        limit,
        sortField: productSortField || undefined,
        sortOrder: (productSortOrder as 'asc' | 'desc') || undefined,
        search: (searchOverride !== undefined ? searchOverride : (productSearchFilter || '').trim()) || undefined,
        customerName: (customerNameOverride !== undefined ? customerNameOverride : selectedCustomerName) || undefined,
      });
      const raw = resp?.products || resp || [];
      const list: ProductMaster[] = raw.map((p: any) => ({
        id: p.id,
        merchantReference: p.merchantReference || '',
        sku: p.itemCode,
        internalReference: p.internalReference || '',
        barcode: p.barcode || '',
        productName: p.itemName,
        productNameEnglish: p.itemNameEnglish,
        productNameChinese: p.itemNameChinese,
        description: p.description,
        pickingRemark: p.pickingRemark,
        brand: p.brand,
        length: p.length != null ? Number(p.length) : undefined,
        width: p.width != null ? Number(p.width) : undefined,
        height: p.height != null ? Number(p.height) : undefined,
        weight: p.weight != null ? Number(p.weight) : undefined,
        packagingLength: p.packagingLength != null ? Number(p.packagingLength) : undefined,
        packagingWidth: p.packagingWidth != null ? Number(p.packagingWidth) : undefined,
        packagingHeight: p.packagingHeight != null ? Number(p.packagingHeight) : undefined,
        packagingWeight: p.packagingWeight != null ? Number(p.packagingWeight) : undefined,
        packageCBM: p.volume != null ? Number(p.volume) : undefined,
        unitCBM: undefined,
        temperatureRequirement: p.temperatureRequirement,
        baseUOM: p.baseUOM || 'PCS',
        packagingUOM: p.packagingUOM || p.baseUOM || 'BOX',
        conversionFactor: p.conversionFactor != null ? Number(p.conversionFactor) : 1,
        lotNumber: (p as any).lotNumber || '',
        serialNumber: (p as any).serialNumber || '',
        batchControlType: (p.batchControlType || 'fifo') as any,
        lotNumberRequired: !!p.lotNumberRequired,
        serialNumberRequired: !!p.serialNumberRequired,
        maxStock: Number(p.maxStock || 0),
        totalStock: Number((p as any).totalStock ?? 0),
        salesChannelRatios: Array.isArray(p.salesChannelRatios) ? p.salesChannelRatios : [],
        isBundle: !!p.isBundle,
        bundleItems: Array.isArray(p.bundleItems)
          ? p.bundleItems.map((it: any) => ({
              id: it.id,
              productId: it.productId,
              quantity: Number(it.quantity || 1),
              sku: it.sku || '',
              productName: it.productName || it.product_name || ''
            }))
          : [],
        multiStoreEnabled: !!p.multiStoreEnabled,
        storeMappings: [],
        isActive: !!p.isActive,
        createdAt: new Date(p.createdAt),
        updatedAt: new Date(p.updatedAt),
        alertEmails: p.alertEmails || [],
        alertEnabled: !!p.alertEnabled,
        productAttributes: Array.isArray(p.productAttributes)
          ? p.productAttributes
          : (typeof p.productAttributes === 'string' && p.productAttributes.trim())
            ? p.productAttributes.split(',').map((s: string) => s.trim()).filter((s: string) => !!s)
            : [],
        supplier: p.supplier || undefined,
        category: Array.isArray(p.category)
          ? p.category
          : (typeof p.category === 'string' && p.category.trim())
            ? p.category.split(',').map((s: string) => s.trim()).filter((s: string) => !!s)
            : [],
        alternateSkuList: p.alternateSkuList || [],
        storeCodes: p.storeCodes || [],
        customerIds: Array.isArray(p.customers) ? p.customers.map((c: any) => c.id) : [],
        customerNames: Array.isArray(p.customers) ? p.customers.map((c: any) => c.name).join(', ') : '',
      }));
      // 套裝下拉需要全量（不受搜尋過濾影響）的清單，這裡保存未經二次過濾的列表
      setAllProductsCache(list);
      // 後端已處理搜尋過濾，直接使用列表
      setProducts(list);
      const total = resp?.pagination?.total ?? list.length;
      setProductTotalCount(total);
      const totalValue = list.reduce((sum, pr) => sum + ((pr.totalStock ?? 0) * 1000), 0);
      const lowStockItems = list.filter(p => (p.totalStock ?? 0) <= p.minStock).length;
      const outOfStockItems = list.filter(p => (p.totalStock ?? 0) === 0).length;
      setKpiStats({ totalProducts: list.length, totalValue, lowStockItems, outOfStockItems });
    } catch (error) {
      message.error(t('加載產品數據失敗'));
    } finally {
      setLoading(false);
    }
  };

  // 為套裝下拉獨立載入候選產品（不帶 SKU 精確過濾）
  const loadBundleCandidates = async () => {
    try {
      const resp: any = await apiService.getProductsV2({ page: 1, limit: 1000 });
      const raw = resp?.products || resp || [];
      const list: ProductMaster[] = raw.map((p: any) => ({
        id: p.id,
        merchantReference: p.merchantReference || '',
        sku: p.itemCode,
        internalReference: p.internalReference || '',
        barcode: p.barcode || '',
        productName: p.itemName,
        productNameEnglish: p.itemNameEnglish,
        productNameChinese: p.itemNameChinese,
        description: p.description,
        pickingRemark: p.pickingRemark,
        brand: p.brand,
        length: p.length != null ? Number(p.length) : undefined,
        width: p.width != null ? Number(p.width) : undefined,
        height: p.height != null ? Number(p.height) : undefined,
        weight: p.weight != null ? Number(p.weight) : undefined,
        packagingLength: p.packagingLength != null ? Number(p.packagingLength) : undefined,
        packagingWidth: p.packagingWidth != null ? Number(p.packagingWidth) : undefined,
        packagingHeight: p.packagingHeight != null ? Number(p.packagingHeight) : undefined,
        packagingWeight: p.packagingWeight != null ? Number(p.packagingWeight) : undefined,
        packageCBM: p.volume != null ? Number(p.volume) : undefined,
        unitCBM: undefined,
        temperatureRequirement: p.temperatureRequirement,
        baseUOM: p.baseUOM || 'PCS',
        packagingUOM: p.packagingUOM || p.baseUOM || 'BOX',
        conversionFactor: p.conversionFactor != null ? Number(p.conversionFactor) : 1,
        lotNumber: (p as any).lotNumber || '',
        serialNumber: (p as any).serialNumber || '',
        batchControlType: (p.batchControlType || 'fifo') as any,
        lotNumberRequired: !!p.lotNumberRequired,
        serialNumberRequired: !!p.serialNumberRequired,
        maxStock: Number(p.maxStock || 0),
        totalStock: Number((p as any).totalStock ?? 0),
        salesChannelRatios: Array.isArray(p.salesChannelRatios) ? p.salesChannelRatios : [],
        isBundle: !!p.isBundle,
        bundleItems: Array.isArray(p.bundleItems)
          ? p.bundleItems.map((it: any) => ({
              id: it.id,
              productId: it.productId,
              quantity: Number(it.quantity || 1),
              sku: it.sku || '',
              productName: it.productName || it.product_name || ''
            }))
          : [],
        multiStoreEnabled: !!p.multiStoreEnabled,
        storeMappings: [],
        isActive: !!p.isActive,
        createdAt: new Date(p.createdAt),
        updatedAt: new Date(p.updatedAt),
        alertEmails: p.alertEmails || [],
        alertEnabled: !!p.alertEnabled,
        productAttributes: Array.isArray(p.productAttributes)
          ? p.productAttributes
          : (typeof p.productAttributes === 'string' && p.productAttributes.trim())
            ? p.productAttributes.split(',').map((s: string) => s.trim()).filter((s: string) => !!s)
            : [],
        supplier: p.supplier || undefined,
        category: Array.isArray(p.category)
          ? p.category
          : (typeof p.category === 'string' && p.category.trim())
            ? p.category.split(',').map((s: string) => s.trim()).filter((s: string) => !!s)
            : []
      }));
      setAllProductsCache(list);
    } catch {
      // 若載入失敗，保留當前快取
    }
  };

  // 套裝下拉伺服器端搜尋（支援搜尋超過1000筆產品）
  const handleBundleProductSearch = async (searchValue: string) => {
    // 清除之前的 timeout
    if (bundleSearchTimeoutRef.current) {
      clearTimeout(bundleSearchTimeoutRef.current);
    }

    // 如果搜尋值少於2個字元，使用本地快取
    if (!searchValue || searchValue.length < 2) {
      setBundleSearchResults([]);
      setBundleSearchLoading(false);
      return;
    }

    setBundleSearchLoading(true);

    // 防抖：延遲300ms執行搜尋
    bundleSearchTimeoutRef.current = setTimeout(async () => {
      try {
        const resp: any = await apiService.getProductsV2({
          search: searchValue,
          page: 1,
          limit: 100
        });
        const raw = resp?.products || resp || [];
        console.log('[Bundle Search] API response:', { searchValue, rawCount: raw.length, raw: raw.slice(0, 3) });
        const list: ProductMaster[] = raw.map((p: any) => {
          // Debug: log isActive value
          if (p.itemCode && p.itemCode.includes('H8442001')) {
            console.log('[Bundle Search] Found target product:', {
              itemCode: p.itemCode,
              isActive: p.isActive,
              is_active: (p as any).is_active,
              isBundle: p.isBundle,
              is_bundle: (p as any).is_bundle,
              unitPrice: p.unitPrice
            });
          }
          return {
          id: p.id,
          merchantReference: p.merchantReference || '',
          sku: p.itemCode,
          internalReference: p.internalReference || '',
          barcode: p.barcode || '',
          productName: p.itemName,
          productNameEnglish: p.itemNameEnglish,
          productNameChinese: p.itemNameChinese,
          description: p.description,
          pickingRemark: p.pickingRemark,
          brand: p.brand,
          length: p.length != null ? Number(p.length) : undefined,
          width: p.width != null ? Number(p.width) : undefined,
          height: p.height != null ? Number(p.height) : undefined,
          weight: p.weight != null ? Number(p.weight) : undefined,
          packagingLength: p.packagingLength != null ? Number(p.packagingLength) : undefined,
          packagingWidth: p.packagingWidth != null ? Number(p.packagingWidth) : undefined,
          packagingHeight: p.packagingHeight != null ? Number(p.packagingHeight) : undefined,
          packagingWeight: p.packagingWeight != null ? Number(p.packagingWeight) : undefined,
          packageCBM: p.volume != null ? Number(p.volume) : undefined,
          unitCBM: undefined,
          temperatureRequirement: p.temperatureRequirement,
          baseUOM: p.baseUOM || 'PCS',
          packagingUOM: p.packagingUOM || p.baseUOM || 'BOX',
          conversionFactor: p.conversionFactor != null ? Number(p.conversionFactor) : 1,
          lotNumber: (p as any).lotNumber || '',
          serialNumber: (p as any).serialNumber || '',
          batchControlType: (p.batchControlType || 'fifo') as any,
          lotNumberRequired: !!p.lotNumberRequired,
          serialNumberRequired: !!p.serialNumberRequired,
          maxStock: Number(p.maxStock || 0),
          totalStock: Number((p as any).totalStock ?? 0),
          salesChannelRatios: Array.isArray(p.salesChannelRatios) ? p.salesChannelRatios : [],
          isBundle: !!p.isBundle,
          bundleItems: [],
          multiStoreEnabled: !!p.multiStoreEnabled,
          storeMappings: [],
          isActive: !!p.isActive,
          createdAt: new Date(p.createdAt),
          updatedAt: new Date(p.updatedAt),
          alertEmails: p.alertEmails || [],
          alertEnabled: !!p.alertEnabled,
          productAttributes: [],
          supplier: p.supplier || undefined,
          category: [],
          alternateSkuList: p.alternateSkuList || [],
          storeCodes: p.storeCodes || []
        };});
        setBundleSearchResults(list);
      } catch {
        setBundleSearchResults([]);
      } finally {
        setBundleSearchLoading(false);
      }
    }, 300);
  };

  // 搜索產品自動完成建議 (SKU/Barcode/Name)
  const handleProductSearch = async (searchValue: string) => {
    if (!searchValue || searchValue.length < 2) {
      setProductSearchOptions([]);
      return;
    }

    try {
      const resp: any = await apiService.getProductsV2({
        search: searchValue,
        page: 1,
        limit: 10
      });
      const raw = resp?.products || resp || [];
      const options = raw.map((p: any) => ({
        value: p.itemCode,  // Use SKU as the primary value
        sku: p.itemCode,
        barcode: p.barcode || '',
        label: `${p.itemCode}${p.barcode ? ' | ' + p.barcode : ''} - ${p.itemName}`
      }));
      setProductSearchOptions(options);
    } catch {
      setProductSearchOptions([]);
    }
  };

  // 導出功能 - 客戶端CSV生成
  /**
   * Two-sheet xlsx: 產品資料 plus 套裝組成 (套裝SKU / 組件SKU / 每套數量).
   * Sheet 2 matches the bulk-import sheet exactly, so the file round-trips —
   * export the recipes, edit them in Excel, re-import to update compositions.
   */
  const handleExportExcel = async () => {
    const hide = message.loading(t('正在準備導出...'), 0);
    try {
      // Page through rather than one large `limit`: the catalogue is ~30k rows
      // and a single capped call silently truncates.
      const PAGE = 1000;
      const all: any[] = [];
      for (let page = 1; ; page++) {
        const resp: any = await apiService.getProductsV2({
          page,
          limit: PAGE,
          ...((productSearchFilter || '').trim() ? { search: (productSearchFilter || '').trim() } : {}),
          ...(selectedCustomerName ? { customerName: selectedCustomerName } : {}),
        });
        const batch: any[] = resp?.products || resp?.data || (Array.isArray(resp) ? resp : []);
        all.push(...batch);
        if (batch.length < PAGE) break;
        // Defensive stop; a runaway pager must not hang the browser.
        if (page >= 100) {
          console.warn('[導出] reached the 100-page ceiling, exporting what was fetched');
          break;
        }
      }

      const normalised = all.map((p: any) => ({
        ...p,
        sku: p.itemCode ?? p.sku,
        productName: p.itemName ?? p.productName,
        productNameEnglish: p.itemNameEnglish ?? p.productNameEnglish,
        productNameChinese: p.itemNameChinese ?? p.productNameChinese,
        totalStock: p.totalStock ?? 0,
        bundleItems: Array.isArray(p.bundleItems) ? p.bundleItems : [],
        alternateSkuList: Array.isArray(p.alternateSkuList) ? p.alternateSkuList : [],
      }));

      const workbook = XLSX.utils.book_new();

      const productSheet = XLSX.utils.aoa_to_sheet([
        [...PRODUCT_HEADERS],
        ...buildProductRows(normalised),
      ]);
      productSheet['!cols'] = PRODUCT_HEADERS.map(() => ({ wch: 16 }));
      XLSX.utils.book_append_sheet(workbook, productSheet, '產品資料');

      const bundleRows = buildBundleRows(normalised);
      const bundleSheet = XLSX.utils.aoa_to_sheet([[...BUNDLE_HEADERS], ...bundleRows]);
      bundleSheet['!cols'] = [{ wch: 22 }, { wch: 22 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(workbook, bundleSheet, '套裝組成');

      // Reference only: the bulk import does not read this sheet.
      const alternateRows = buildAlternateRows(normalised);
      const alternateSheet = XLSX.utils.aoa_to_sheet([[...ALTERNATE_HEADERS], ...alternateRows]);
      alternateSheet['!cols'] = [{ wch: 22 }, { wch: 22 }];
      XLSX.utils.book_append_sheet(workbook, alternateSheet, '備用SKU');

      XLSX.writeFile(workbook, `products_export_${new Date().toISOString().split('T')[0]}.xlsx`);

      hide();
      const extras: string[] = [];
      if (bundleRows.length > 0) extras.push(t('{{n}} 筆套裝組成', { n: bundleRows.length }));
      if (alternateRows.length > 0) extras.push(t('{{n}} 筆備用SKU', { n: alternateRows.length }));
      message.success(
        extras.length > 0
          ? t('已導出 {{products}} 個產品（{{extras}}）', {
              products: normalised.length,
              extras: extras.join('、'),
            })
          : t('已導出 {{products}} 個產品', { products: normalised.length }),
      );
    } catch (error) {
      hide();
      console.error('導出失敗:', error);
      message.error(t('導出失敗，請稍後重試'));
    }
  };

  // 生成CSV內容
  // CBM自動計算函數 - 改為使用包裝尺寸
  const calculateCBM = () => {
    const packagingLength = form.getFieldValue('packagingLength');
    const packagingWidth = form.getFieldValue('packagingWidth');
    const packagingHeight = form.getFieldValue('packagingHeight');
    const conversionFactor = form.getFieldValue('conversionFactor');

    if (packagingLength && packagingWidth && packagingHeight) {
      // 計算包裝體積 (cm³ 轉 m³)
      const packageCBM = (packagingLength * packagingWidth * packagingHeight) / 1000000;
      form.setFieldsValue({ packageCBM: packageCBM });

      // 如果有轉換因子，計算單位體積
      if (conversionFactor && conversionFactor > 0) {
        const unitCBM = packageCBM / conversionFactor;
        form.setFieldsValue({ unitCBM: unitCBM });
      }
    } else {
      // 清空CBM字段如果包裝尺寸不完整
      form.setFieldsValue({ 
        packageCBM: undefined,
        unitCBM: undefined
      });
    }
  };

  // 散件模式：將包裝尺寸與重量自動同步為產品尺寸與重量，並設置單位轉換
  const syncPackagingFromProduct = () => {
    try {
      const length = form.getFieldValue('length');
      const width = form.getFieldValue('width');
      const height = form.getFieldValue('height');
      const weight = form.getFieldValue('weight');
      const baseUOM = form.getFieldValue('baseUOM');

      form.setFieldsValue({
        packagingLength: length,
        packagingWidth: width,
        packagingHeight: height,
        packagingWeight: weight,
        packagingUOM: baseUOM,
        conversionFactor: 1,
      });
      calculateCBM();
    } catch {}
  };

  // 監聽散件開關
  useEffect(() => {
    // 當切換為「未包裝」（isPackaged=false）時，同步包裝尺寸
    if (isPackagedEnabled === false) {
      syncPackagingFromProduct();
    }
  }, [isPackagedEnabled]);

  // 表單值變更時的聯動處理
  const handleValuesChange = (changedValues: any, allValues: any) => {
    const changedKeys = Object.keys(changedValues || {});
    if (allValues?.isPackaged === false) {
      const needSync = ['length', 'width', 'height', 'weight', 'baseUOM', 'isPackaged'].some(k => changedKeys.includes(k));
      if (needSync) {
        syncPackagingFromProduct();
      }
    }
  };

  // 計算KPI統計
  const calculateKPIStats = () => {
    const totalProducts = products.length;
    const totalValue = products.reduce((sum, product) => sum + ((product.totalStock ?? 0) * 1000), 0);
    const lowStockItems = products.filter(p => (p.totalStock ?? 0) <= (p as any).minStock).length;
    const outOfStockItems = products.filter(p => (p.totalStock ?? 0) === 0).length;

    setKpiStats({
      totalProducts,
      totalValue,
      lowStockItems,
      outOfStockItems,
    });
  };

  // 計算CBM


  // 生成內部參考
  const generateInternalReference = (): string => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    return `INT-${new Date().getFullYear()}-${String(timestamp).slice(-6)}${String(random).padStart(3, '0')}`;
  };

  // 處理創建
  const handleCreate = () => {
    setModalTitle(t('創建新產品'));
    setEditingProduct(null);
    setIsReadOnly(false);
    form.resetFields();
    setBundleEnabled(false);
    setBarcodeList([]);
    // 新產品不是子 SKU
    setEditingIsChildSku(false);
    setEditingParentSkuInfo(null);
    setAlternateSkuSearchResults([]);
    form.setFieldsValue({
      internalReference: generateInternalReference(),
      isActive: true,
      alertEnabled: true,
      alertEmails: ['admin@company.com'],
      multiStoreEnabled: false,
      batchControlType: BatchControlType.FIFO,
      lotNumberRequired: false,
      serialNumberRequired: false,
      conversionFactor: 1,
      maxStock: 1000,
      isBundle: false,
      bundleItems: [],
      storeCodes: [],
      customerIds: [],
    });
    setAlternateSkuList([]);
    setModalVisible(true);
    // 應用打開時預載入候選產品（不受 SKU 過濾）
    loadBundleCandidates();
    // Load available store codes
    loadAvailableStoreCodes();
    // Load available customers
    loadAvailableCustomers();
  };

  // 處理編輯
  const handleEdit = (product: ProductMaster) => {
    setModalTitle(t('編輯產品'));
    setEditingProduct(product);
    setIsReadOnly(false);
    setModalVisible(true);
    // 編輯時同樣確保候選產品就緒
    loadBundleCandidates();
    // Load available store codes
    loadAvailableStoreCodes();
    // Load available customers
    loadAvailableCustomers();

    // 設置子 SKU 狀態（用於禁用備用 SKU 功能）
    setEditingIsChildSku(!!product.isChildSku);
    setEditingParentSkuInfo(product.parentSkuInfo || null);
    // 清空備用 SKU 搜尋結果
    setAlternateSkuSearchResults([]);

    // 先顯示加載狀態或使用列表數據作為臨時顯示（可選）
    const productWithBundleItems = {
      ...product,
      bundleItems: product.bundleItems || []
    };
    form.setFieldsValue(productWithBundleItems);
    setAlternateSkuList(productWithBundleItems.alternateSkuList)
    setBundleEnabled(!!product.isBundle);
    
    // 延遲計算CBM，確保表單值已設置
    setTimeout(() => {
      calculateCBM();
    }, 100);

    // 立即從後端拉取最新資料並回填，確保數據庫直接修改的值能正確顯示
    (async () => {
      try {
        const p: any = await apiService.getProductByIdV2(product.id);
        lastDetailRef.current = p;
        const existing = form.getFieldsValue();
        const mapped: any = {
          merchantReference: (p.merchantReference != null && p.merchantReference !== '') ? p.merchantReference : (existing.merchantReference || ''),
          sku: p.itemCode,
          internalReference: (p.internalReference != null && p.internalReference !== '') ? p.internalReference : (existing.internalReference || ''),
          barcode: (p.barcode != null && p.barcode !== '') ? p.barcode : (existing.barcode || ''),
          productName: p.itemName,
          brand: (p.brand != null && p.brand !== '') ? p.brand : (existing.brand || ''),
          description: p.description,
          pickingRemark: p.pickingRemark,
          isActive: (p.isActive !== undefined ? !!p.isActive : (existing.isActive ?? true)),
          length: p.length != null ? Number(p.length) : undefined,
          width: p.width != null ? Number(p.width) : undefined,
          height: p.height != null ? Number(p.height) : undefined,
          weight: p.weight != null ? Number(p.weight) : undefined,
          packagingLength: p.packagingLength != null ? Number(p.packagingLength) : undefined,
          packagingWidth: p.packagingWidth != null ? Number(p.packagingWidth) : undefined,
          packagingHeight: p.packagingHeight != null ? Number(p.packagingHeight) : undefined,
          packagingWeight: p.packagingWeight != null ? Number(p.packagingWeight) : undefined,
          packageCBM: p.volume != null ? Number(p.volume) : undefined,
          baseUOM: p.baseUOM || 'PCS',
          packagingUOM: p.packagingUOM || p.baseUOM || 'BOX',
          conversionFactor: p.conversionFactor != null ? Number(p.conversionFactor) : 1,
          productNameEnglish: p.itemNameEnglish,
          productNameChinese: p.itemNameChinese,
          batchControlType: p.batchControlType || 'fifo',
          lotNumberRequired: !!p.lotNumberRequired,
          serialNumberRequired: !!p.serialNumberRequired,
          totalStock: (p as any).totalStock != null ? Number((p as any).totalStock) : 0,
          lotNumber: p.lotNumber || (p as any).lot_number || '',
          serialNumber: p.serialNumber || (p as any).serial_number || '',
          maxStock: Number(p.maxStock || 0),
          isBundle: !!p.isBundle,
          bundleItems: Array.isArray((p as any).bundleItems) ? (p as any).bundleItems.map((it: any) => ({ productId: it.productId, quantity: Number(it.quantity || 1), sku: it.sku, productName: it.productName })) : [],
          alertEnabled: !!(p as any).alertEnabled,
          alertEmails: Array.isArray((p as any).alertEmails)
            ? (p as any).alertEmails
            : (typeof (p as any).alertEmails === 'string' && (p as any).alertEmails.trim())
              ? (p as any).alertEmails.split(',').map((s: string) => s.trim()).filter(Boolean)
              : (Array.isArray(existing.alertEmails) ? existing.alertEmails : []),
          multiStoreEnabled: !!(p as any).multiStoreEnabled,
          supplier: p.supplier || undefined,
          category: Array.isArray(p.category) ? p.category : (typeof p.category === 'string' && p.category.trim()) ? p.category.split(',').map((s: string) => s.trim()).filter((s: string) => !!s) : [],
          alternateSkuList: p.alternateSkuList || [],
          storeCodes: Array.isArray(p.storeCodes) ? p.storeCodes : [],
          customerIds: Array.isArray(p.customerIds) ? p.customerIds : [],
          // 國際運輸/海關資料
          hsCode: (p as any).hsCode || undefined,
          origin: p.origin || undefined,
          declaredValue: (p as any).declaredValue != null ? Number((p as any).declaredValue) : undefined,
        };
        form.setFieldsValue(mapped);
        try {
          const initList = String(mapped.barcode || '')
            .split(/[\s,;]+/)
            .map(s => s.trim())
            .filter(Boolean);
          setBarcodeList(initList);
          syncBarcodeField(initList);
        } catch {}
        setAlternateSkuList(mapped.alternateSkuList)
        setBundleEnabled(!!mapped.isBundle);
        // 更新子 SKU 狀態（從後端取得最新資料）
        setEditingIsChildSku(!!(p as any).isChildSku);
        setEditingParentSkuInfo((p as any).parentSkuInfo || null);
        // 強制覆蓋三個開關，避免任何預設值覆蓋
        form.setFieldsValue({
          isActive: !!(p as any).isActive,
          alertEnabled: !!(p as any).alertEnabled,
          multiStoreEnabled: !!(p as any).multiStoreEnabled,
        });
      } catch {}
    })();
  };

  // 處理查看
  const handleView = (product: ProductMaster) => {
    setModalTitle(t('產品詳情'));
    setEditingProduct(product);
    setIsReadOnly(true);
    // 設置子 SKU 狀態
    setEditingIsChildSku(!!product.isChildSku);
    setEditingParentSkuInfo(product.parentSkuInfo || null);
    setAlternateSkuSearchResults([]);
    // 確保bundleItems存在
    const productWithBundleItems = {
      ...product,
      bundleItems: product.bundleItems || []
    };
    form.setFieldsValue(productWithBundleItems);
    setAlternateSkuList(productWithBundleItems.alternateSkuList)
    try {
      const initList = String(productWithBundleItems.barcode || '')
        .split(/[\s,;]+/)
        .map((s: string) => s.trim())
        .filter((s: string) => !!s);
      setBarcodeList(initList);
      syncBarcodeField(initList);
    } catch {}
    setBundleEnabled(!!product.isBundle);
    setModalVisible(true);
    // 查看時也預載入一次，方便切換為編輯再用
    loadBundleCandidates();
    // Load available store codes
    loadAvailableStoreCodes();
    // Load available customers
    loadAvailableCustomers();
    // 延遲計算CBM，確保表單值已設置
    setTimeout(() => {
      calculateCBM();
    }, 100);

    // 兼容：查看詳情時也拉取一次保險，避免列表映射遺漏
    (async () => {
      try {
        const p: any = await apiService.getProductByIdV2(product.id);
        const mapped: any = {
          merchantReference: p.merchantReference || '',
          sku: p.itemCode,
          internalReference: p.internalReference || '',
          barcode: p.barcode || '',
          productName: p.itemName,
          brand: p.brand,
          description: p.description,
          pickingRemark: p.pickingRemark,
          isActive: (p.isActive !== undefined ? !!p.isActive : true),
          length: p.length != null ? Number(p.length) : undefined,
          width: p.width != null ? Number(p.width) : undefined,
          height: p.height != null ? Number(p.height) : undefined,
          weight: p.weight != null ? Number(p.weight) : undefined,
          packagingLength: p.packagingLength != null ? Number(p.packagingLength) : undefined,
          packagingWidth: p.packagingWidth != null ? Number(p.packagingWidth) : undefined,
          packagingHeight: p.packagingHeight != null ? Number(p.packagingHeight) : undefined,
          packagingWeight: p.packagingWeight != null ? Number(p.packagingWeight) : undefined,
          packageCBM: p.volume != null ? Number(p.volume) : undefined,
          baseUOM: p.baseUOM || 'PCS',
          packagingUOM: p.packagingUOM || p.baseUOM || 'BOX',
          conversionFactor: p.conversionFactor != null ? Number(p.conversionFactor) : 1,
          productNameEnglish: p.itemNameEnglish,
          productNameChinese: p.itemNameChinese,
          batchControlType: p.batchControlType || 'fifo',
          lotNumberRequired: !!p.lotNumberRequired,
          serialNumberRequired: !!p.serialNumberRequired,
          totalStock: (p as any).totalStock != null ? Number((p as any).totalStock) : 0,
          lotNumber: p.lotNumber || (p as any).lot_number || '',
          serialNumber: p.serialNumber || (p as any).serial_number || '',
          maxStock: Number(p.maxStock || 0),
          isBundle: !!p.isBundle,
          alertEnabled: !!p.alertEnabled,
          alertEmails: Array.isArray(p.alertEmails) ? p.alertEmails : [],
          multiStoreEnabled: !!p.multiStoreEnabled,
          supplier: p.supplier || undefined,
          category: Array.isArray(p.category) ? p.category : (typeof p.category === 'string' && p.category.trim()) ? p.category.split(',').map((s: string) => s.trim()).filter((s: string) => !!s) : [],
          storeCodes: Array.isArray(p.storeCodes) ? p.storeCodes : [],
          customerIds: Array.isArray(p.customerIds) ? p.customerIds : [],
        };
        form.setFieldsValue(mapped);
        setBundleEnabled(!!mapped.isBundle);
      } catch {}
    })();
  };

  // 處理刪除（改為調用後端 API）
  const handleDelete = (product: ProductMaster) => {
    Modal.confirm({
      title: t('確認刪除'),
      content: t('確定要刪除產品 "{{productName}}" 嗎？', { productName: product.productName }),
      okText: t('刪除'),
      okButtonProps: { danger: true },
      cancelText: t('取消'),
      onOk: async () => {
        try {
          setLoading(true);
          try {
            await apiService.deleteProductV2(product.id);
          } catch (err: any) {
            // 若後端因關聯阻擋，提示並自動改用強制刪除
            const msg = String(err?.message || '');
            if (msg.includes('existing transactions')) {
              await apiService.deleteProductV2(product.id, { force: true });
            } else {
              throw err;
            }
          }
          message.success(t('產品刪除成功'));
          await loadProducts();
        } catch (e: any) {
          message.error(t('刪除失敗：{{msg}}', { msg: e?.message || t('未知錯誤') }));
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // 處理模態框確認
  const handleModalOk = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue(true);
      // 強制讀取三個開關的即時值，避免未同步造成的假值
      const isActiveNow = form.getFieldValue('isActive');
      const alertEnabledNow = form.getFieldValue('alertEnabled');
      const multiStoreEnabledNow = form.getFieldValue('multiStoreEnabled');
      // 確保四個字段從表單直接取值為字串，避免被 undefined 傳遞
      if (typeof values.merchantReference !== 'string') values.merchantReference = form.getFieldValue('merchantReference') ?? '';
      if (typeof values.internalReference !== 'string') values.internalReference = form.getFieldValue('internalReference') ?? '';
      if (typeof values.barcode !== 'string') values.barcode = form.getFieldValue('barcode') ?? '';
      if (typeof values.brand !== 'string') values.brand = form.getFieldValue('brand') ?? '';
      const detail = (lastDetailRef.current || {}) as any;
      
      // 若散件：確保單位與轉換、包裝尺寸一致
      if (values.isPackaged === false) {
        values.packagingLength = values.length;
        values.packagingWidth = values.width;
        values.packagingHeight = values.height;
        values.packagingWeight = values.weight;
        values.packagingUOM = values.baseUOM;
        values.conversionFactor = 1;
      }
      // 自動計算CBM（使用包裝尺寸；散件時也會被上面同步）
      if (values.packagingLength && values.packagingWidth && values.packagingHeight) {
        // 計算包裝體積 (cm³ 轉 m³)
        values.packageCBM = (values.packagingLength * values.packagingWidth * values.packagingHeight) / 1000000;
        values.unitCBM = values.packageCBM / (values.conversionFactor || 1);
      }

      // 當啟用套裝時，要求至少一條套裝商品
      const bundleItems: any[] = form.getFieldValue('bundleItems') || [];
      const isBundleNow: boolean = !!form.getFieldValue('isBundle');
      if (isBundleNow) {
        const validItems = (bundleItems || []).filter(it => it && it.productId && Number(it.quantity) > 0);
        if (validItems.length === 0) {
          message.error(t('請先添加至少一個套裝商品'));
          return;
        }
      }

      // 讀取登入使用者的 companyId（優先 auth-storage，其次 current-company-id）
      const auth = localStorage.getItem('auth-storage');
      const parsed = (() => { try { return auth ? JSON.parse(auth) : null; } catch { return null; } })();
      const companyId = parsed?.state?.user?.companyId || localStorage.getItem('current-company-id') || undefined;

      const payload = {
        itemName: values.productName,
        itemCode: values.sku,
        companyId,
        description: values.description,
        pickingRemark: values.pickingRemark,
        unitPrice: 0,
        minStock: 0,
        merchantReference: String(values.merchantReference ?? ''),
        internalReference: String(values.internalReference ?? ''),
        barcode: String(values.barcode ?? ''),
        brand: String(values.brand ?? ''),
        length: values.length,
        width: values.width,
        height: values.height,
        weight: values.weight,
        packagingLength: values.packagingLength,
        packagingWidth: values.packagingWidth,
        packagingHeight: values.packagingHeight,
        packagingWeight: values.packagingWeight,
        temperatureRequirement: values.temperatureRequirement,
        baseUOM: values.baseUOM,
        packagingUOM: values.isPackaged === false ? values.baseUOM : values.packagingUOM,
        conversionFactor: values.isPackaged === false ? 1 : values.conversionFactor,
        itemNameEnglish: values.productNameEnglish,
        itemNameChinese: values.productNameChinese,
        batchControlType: values.batchControlType,
        lotNumberRequired: !!values.lotNumberRequired,
        serialNumberRequired: !!values.serialNumberRequired,
        maxStock: values.maxStock != null ? Number(values.maxStock) : 0,
        isBundle: isBundleNow,
        bundleItems: (bundleItems || []).map((it: any) => ({ productId: it.productId, quantity: Math.max(1, Number(it.quantity || 1)) })),
        productAttributes: values.productAttributes || [],
        supplier: values.supplier,
        category: values.category || [],
        // 國際運輸/海關資料
        hsCode: values.hsCode || undefined,
        origin: values.origin || undefined,
        declaredValue: values.declaredValue != null ? Number(values.declaredValue) : undefined,
        lotNumber: values.lotNumber,
        serialNumber: values.serialNumber,
        alertEmails: values.alertEmails || [],
        alertEnabled: !!alertEnabledNow,
        multiStoreEnabled: !!multiStoreEnabledNow,
        isActive: isActiveNow !== undefined ? !!isActiveNow : true,
        salesChannelRatios: (values.salesChannelRatios || []).map((r: any) => ({ channel: r.channel, ratio: Number(r.ratio || 0) })),
        alternateSkuList,
        storeCodes: values.storeCodes || [],
        customerIds: values.customerIds || [], // ✅ 改为 customerIds 数组
      } as any;

      if (editingProduct) {
        await apiService.updateProductV2(editingProduct.id, payload);
        message.success(t('產品更新成功'));
      } else {
        await apiService.createProductV2(payload);
        message.success(t('產品創建成功'));
      }

      await loadProducts();
      
      setModalVisible(false);
      form.resetFields();
    } catch (errorInfo) {
      console.log('Submit failed:', errorInfo);
      // 優先處理 API 錯誤：顯示服務端返回的錯誤訊息
      const apiMsg = (errorInfo as any)?.message || (errorInfo as any)?.error || (errorInfo as any)?.response?.data?.error || (errorInfo as any)?.response?.data?.message;
      if (apiMsg && typeof apiMsg === 'string') {
        if (/Product code already exists/i.test(apiMsg) || /已存在/.test(apiMsg)) {
          try {
            form.setFields([{ name: 'sku', errors: [t('SKU 已存在，請更換一個唯一的 SKU')] }]);
          } catch {}
          notification.error({
            message: t('SKU 已存在'),
            description: t('該 SKU 已存在，請更換一個唯一的 SKU。'),
            duration: 6,
          });
          return;
        }
        notification.error({
          message: t('提交失敗'),
          description: apiMsg,
          duration: 6,
        });
        return;
      }

      const errs = (errorInfo as any)?.errorFields || [];
      console.log('errs:' + errs)
      if (Array.isArray(errs) && errs.length > 0) {
        // 友好名稱映射（可按需補充）
        const fieldNameMap: Record<string, string> = {
          merchantReference: t('商家參考'),
          sku: 'SKU ID',
          productName: t('產品名稱'),
          barcode: t('條形碼'),
          brand: t('品牌'),
          baseUOM: t('基本單位'),
          packagingUOM: t('包裝單位'),
          conversionFactor: t('轉換因子'),
          maxStock: t('最大庫存'),
          batchControlType: t('批次控制策略'),
          length: t('產品長度'),
          width: t('產品寬度'),
          height: t('產品高度'),
          weight: t('產品重量'),
          productNameEnglish: t('英文名稱'),
          productNameChinese: t('中文名稱'),
          supplier: t('供應商'),
          category: t('產品分類'),
        };

        const toKey = (n: any) => Array.isArray(n) ? String(n[0]) : String(n);
        const missingKeys: string[] = Array.from(new Set(errs.map((e: any) => toKey(e.name))));
        const missingLabels = missingKeys.map(k => fieldNameMap[k] || k);

        // 提示具體缺失字段
        notification.error({
          message: t('缺少必填字段'),
          description: missingLabels.join(t('、')),
          duration: 6,
        });

        // 聚焦第一個錯誤並顯示具體訊息
        const first = errs[0];
        const firstKey = toKey(first.name);
        const firstMsg = first.errors && first.errors[0] ? first.errors[0] : t('必填');
        // 滾動到第一個錯誤
        try { form.scrollToField(first.name); } catch {}
        message.error(t('請檢查「{{field}}」: {{msg}}', { field: fieldNameMap[firstKey] || firstKey, msg: firstMsg }));
      } else {
        message.error(t('請檢查表單輸入，確保填寫所有必填字段'));
      }
    }
  };

  // 處理條形碼掃描
  const handleBarcodeScan = () => {
    // 模擬條形碼掃描
    const scannedBarcode = `BAR-${Date.now()}`;
    const next = normalizeBarcodes([...(barcodeList || []), scannedBarcode]);
    setBarcodeList(next);
    syncBarcodeField(next);
    message.success(t('掃描成功並已追加: {{scannedBarcode}}', { scannedBarcode }));
  };

  // 複製內部參考
  const handleCopyInternalReference = () => {
    const internalReference = form.getFieldValue('internalReference');
    if (internalReference) {
      navigator.clipboard.writeText(internalReference).then(() => {
        message.success(t('內部參考已複製到剪貼板'));
      }).catch(() => {
        // 降級方案：使用舊的複製方法
        const textArea = document.createElement('textarea');
        textArea.value = internalReference;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        message.success(t('內部參考已複製到剪貼板'));
      });
    } else {
      message.warning(t('沒有可複製的內部參考'));
    }
  };

  // 按SKU ID快速加入套裝
  const handleQuickAddSkuToBundle = () => {
    const sku: string = form.getFieldValue('quickAddSKU');
    const qtyRaw: number = form.getFieldValue('quickAddQty');
    const quantity: number = Number(qtyRaw) > 0 ? Number(qtyRaw) : 1;

    if (!sku || sku.toString().trim() === '') {
      message.warning(t('請輸入SKU ID'));
      return;
    }

    const product = products.find(p => p.sku === sku);
    if (!product) {
      message.warning(t('未找到 SKU ID 為 "{{sku}}" 的產品', { sku }));
      return;
    }

    if (editingProduct && product.id === editingProduct.id) {
      message.warning(t('不能將當前產品加入自身套裝'));
      return;
    }

    if (!product.isActive || product.isBundle) {
      message.warning(t('該產品不可加入套裝（需為活躍且非套裝）'));
      return;
    }

    const currentItems = form.getFieldValue('bundleItems') || [];
    const existIndex = currentItems.findIndex((it: any) => it?.productId === product.id);
    if (existIndex >= 0) {
      const newItems = [...currentItems];
      newItems[existIndex] = {
        ...newItems[existIndex],
        quantity: (Number(newItems[existIndex]?.quantity) || 0) + quantity,
      };
      form.setFieldsValue({ bundleItems: newItems });
    } else {
      const newItem = {
        productId: product.id,
        productName: product.productName,
        sku: product.sku,
        baseUOM: product.baseUOM,
        quantity: quantity,
      };
      form.setFieldsValue({ bundleItems: [...currentItems, newItem] });
    }

    form.setFieldsValue({ quickAddSKU: undefined, quickAddQty: 1 });
    message.success(t('已按 SKU ID 添加到套裝'));
  };

  // 按產品ID快速加入套裝
  const handleQuickAddProductById = () => {
    const productId: string = form.getFieldValue('quickAddProductId');
    const qtyRaw: number = form.getFieldValue('quickAddQty');
    const quantity: number = Number(qtyRaw) > 0 ? Number(qtyRaw) : 1;

    if (!productId) {
      message.warning(t('請先選擇產品'));
      return;
    }

    const product = products.find(p => p.id === productId);
    if (!product) {
      message.warning(t('未找到所選產品'));
      return;
    }

    if (editingProduct && product.id === editingProduct.id) {
      message.warning(t('不能將當前產品加入自身套裝'));
      return;
    }

    if (!product.isActive || product.isBundle) {
      message.warning(t('該產品不可加入套裝（需為活躍且非套裝）'));
      return;
    }

    const currentItems = form.getFieldValue('bundleItems') || [];
    const existIndex = currentItems.findIndex((it: any) => it?.productId === product.id);
    if (existIndex >= 0) {
      const newItems = [...currentItems];
      newItems[existIndex] = {
        ...newItems[existIndex],
        quantity: (Number(newItems[existIndex]?.quantity) || 0) + quantity,
      };
      form.setFieldsValue({ bundleItems: newItems });
    } else {
      const newItem = {
        productId: product.id,
        productName: product.productName,
        sku: product.sku,
        baseUOM: product.baseUOM,
        quantity: quantity,
      };
      form.setFieldsValue({ bundleItems: [...currentItems, newItem] });
    }

    form.setFieldsValue({ quickAddProductId: undefined, quickAddQty: 1 });
    message.success(t('已添加到套裝'));
  };

  // 批量上傳狀態
  const [uploadStatus, setUploadStatus] = useState<{
    isProcessing: boolean;
    data: any[];
    errors: Array<{row: number; field: string; message: string; value: any}>;
    hasErrors: boolean;
  }>({
    isProcessing: false,
    data: [],
    errors: [],
    hasErrors: false
  });
  
  // 已移除頁面縮放功能
  
  // 批量上傳Modal狀態
  const [bulkUploadVisible, setBulkUploadVisible] = useState(false);
  
  // 已移除頁面縮放控制邏輯

  // 處理批量上傳完成
  const handleBulkUploadComplete = (
    uploadedProducts: any[],
    bundleComponents: Array<{ parentSku: string; componentSku: string; quantity: number }> = [],
  ) => {
    (async () => {
      try {
        setLoading(true);
        // 從 localStorage 讀取 companyId
        const auth = localStorage.getItem('auth-storage');
        const parsed = (() => { try { return auth ? JSON.parse(auth) : null; } catch { return null; } })();
        const companyId = parsed?.state?.user?.companyId || localStorage.getItem('current-company-id') || undefined;

        // 構建後端批量建立 payload（對應 /products-v2/bulk）
        const productsPayload = uploadedProducts.map((item: any) => ({
          itemName: item.productName,
          itemCode: item.sku,
          description: item.description,
          merchantReference: item.merchantReference,
          internalReference: undefined,
          barcode: item.barcode,
          brand: item.brand,
          length: item.length,
          width: item.width,
          height: item.height,
          weight: item.weight,
          packagingLength: item.packagingLength,
          packagingWidth: item.packagingWidth,
          packagingHeight: item.packagingHeight,
          packagingWeight: item.packagingWeight,
          temperatureRequirement: item.temperatureRequirement,
          baseUOM: item.baseUOM,
          packagingUOM: item.packagingUOM || item.baseUOM,
          conversionFactor: item.conversionFactor || 1,
          itemNameEnglish: item.productNameEnglish,
          itemNameChinese: item.productNameChinese,
          maxStock: Number(item.maxStock) || 0,
          customer: item.customer || undefined,
          storeCodes: item.storeCode ? String(item.storeCode).split(',').map((s: string) => s.trim()).filter(Boolean) : undefined,
          batchControlType: item.batchControlType || undefined,
          companyId,
        }));

        const response: any = await apiService.bulkCreateProducts(productsPayload, bundleComponents);

        // 關閉模態框
        setBulkUploadVisible(false);

        // 根據實際結果顯示消息
        const results = response?.results || response;
        const successful = results?.successful ?? productsPayload.length;
        const failed = results?.failed ?? 0;
        const errors = results?.errors || [];
        const bundles = results?.bundles || { total: 0, successful: 0, failed: 0, errors: [] };
        const skippedExistingParents = results?.skippedExistingParents ?? 0;

        if (bundles.total > 0) {
          if (bundles.failed === 0) {
            message.success(t('成功設定 {{count}} 個套裝組成', { count: bundles.successful }));
          } else {
            message.warning(
              t('套裝組成：{{successful}} 個成功，{{failed}} 個失敗', {
                successful: bundles.successful,
                failed: bundles.failed,
              }),
            );
            const bundleDetails = (bundles.errors || [])
              .slice(0, 5)
              .map((e: any) => t('套裝 {{sku}}：{{error}}', { sku: e.parentSku, error: e.error }))
              .join('\n');
            console.warn('Bundle import errors:', bundles.errors);
            Modal.warning({
              title: t('部分套裝組成設定失敗'),
              content: (
                <div style={{ maxHeight: 300, overflow: 'auto' }}>
                  <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>{bundleDetails}</pre>
                </div>
              ),
            });
          }
        }

        if (skippedExistingParents > 0) {
          message.info(
            t('{{count}} 個既有套裝SKU僅更新套裝組成，未重複建立產品', { count: skippedExistingParents }),
          );
        }

        if (failed === 0) {
          // A bundle-only file has no product rows; "成功導入 0 個產品" would be noise.
          if (successful > 0 || bundles.total === 0) {
            message.success(t('成功導入 {{successful}} 個產品', { successful }));
          }
        } else if (successful > 0) {
          // 部分成功
          message.warning(t('導入完成：{{successful}} 個成功，{{failed}} 個失敗', { successful, failed }));
          // 顯示失敗詳情
          if (errors.length > 0) {
            const errorDetails = errors.slice(0, 5).map((e: any) => t('第{{row}}行: {{error}}', { row: e.row, error: e.error })).join('\n');
            const moreText = errors.length > 5 ? t('\n...及其他 {{count}} 個錯誤', { count: errors.length - 5 }) : '';
            console.warn('Bulk import errors:', errors);
            Modal.warning({
              title: t('部分產品導入失敗'),
              content: (
                <div style={{ maxHeight: 300, overflow: 'auto' }}>
                  <p>{t('以下產品導入失敗：')}</p>
                  <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>{errorDetails}{moreText}</pre>
                </div>
              ),
            });
          }
        } else {
          // 全部失敗
          message.error(t('導入失敗：{{failed}} 個產品均無法導入', { failed }));
          if (errors.length > 0) {
            const errorDetails = errors.slice(0, 5).map((e: any) => t('第{{row}}行: {{error}}', { row: e.row, error: e.error })).join('\n');
            console.error('Bulk import all failed:', errors);
            Modal.error({
              title: t('產品導入失敗'),
              content: (
                <div style={{ maxHeight: 300, overflow: 'auto' }}>
                  <p>{t('所有產品導入失敗：')}</p>
                  <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>{errorDetails}</pre>
                </div>
              ),
            });
          }
        }

        // 重置到第一頁並重新加載數據
        setProductCurrentPage(1);
        await loadProducts(1, productPageSize);
      } catch (e: any) {
        console.error('Bulk create products failed:', e);
        message.error(t('批量上傳失敗：{{msg}}', { msg: e?.message || t('請稍後重試') }));
        setBulkUploadVisible(false);
      } finally {
        setLoading(false);
      }
    })();
  };

  // 產品字段驗證規則
  const validateProductField = (field: string, value: any, rowIndex: number): {isValid: boolean; message?: string} => {
    const errors: string[] = [];
    
    switch (field) {
      case 'sku':
        if (!value || value.toString().trim() === '') {
          errors.push(t('SKU是必填字段'));
        } else if (products.some(p => p.sku === value.toString().trim())) {
          errors.push(t('SKU "{{value}}" 已存在，請使用唯一的SKU', { value }));
        }
        break;
        
      case 'productName':
        if (!value || value.toString().trim() === '') {
          errors.push(t('產品名稱是必填字段'));
        } else if (value.toString().length < 2) {
          errors.push(t('產品名稱至少需要2個字符'));
        }
        break;
        
      case 'baseUOM':
        if (!value || value.toString().trim() === '') {
          errors.push(t('基本單位是必填字段'));
        }
        break;
        
      case 'weight':
        if (value && value !== '') {
          const weight = Number(value);
          if (isNaN(weight) || weight < 0) {
            errors.push(t('重量必須是非負數'));
          }
        }
        break;
        
      case 'length':
      case 'width':
      case 'height':
        if (value && value !== '') {
          const dimension = Number(value);
          if (isNaN(dimension) || dimension < 0) {
            errors.push(t('{{dimension}}必須是非負數', { dimension: field === 'length' ? t('長度') : field === 'width' ? t('寬度') : t('高度') }));
          }
        }
        break;
        
      case 'conversionFactor':
        if (value && value !== '') {
          const factor = Number(value);
          if (isNaN(factor) || factor <= 0) {
            errors.push(t('轉換因子必須是正數'));
          }
        }
        break;
        
      case 'sellingPrice':
        if (value && value !== '') {
          const price = Number(value);
          if (isNaN(price) || price < 0) {
            errors.push(t('銷售價格必須是非負數'));
          }
        }
        break;
    }
    
    return {
      isValid: errors.length === 0,
      message: errors.join('; ')
    };
  };

  // 處理批量上傳文件解析和驗證
  const processBatchUploadFile = async (file: File): Promise<{data: any[], errors: Array<{row: number; field: string; message: string; value: any}>}> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          // 模擬CSV/Excel解析 - 在實際實現中，您會使用如 PapaParse 或 xlsx 等庫
          const text = e.target?.result as string;
          const lines = text.split('\n').filter(line => line.trim());
          
          if (lines.length < 2) {
            resolve({
              data: [],
              errors: [{row: 0, field: 'file', message: t('文件必須包含標題行和至少一行數據'), value: ''}]
            });
            return;
          }
          
          // 模擬解析的測試數據（包含一些錯誤的記錄）
          const mockParsedData = [
            {
              merchantReference: 'MERCH-UPLOAD-001',
              sku: 'SKU-UPLOAD-001',
              productName: '批量上傳產品1',
              productNameEnglish: 'Bulk Upload Product 1',
              baseUOM: 'PCS',
              currentStock: 100,
              reorderLevel: 20,
              weight: 0.5,
              length: 10,
              width: 8,
              height: 3,
              sellingPrice: 99.99
            },
            {
              merchantReference: 'MERCH-UPLOAD-002', 
              sku: '', // 錯誤：空 SKU ID
              productName: '批量上傳產品2',
              productNameEnglish: 'Bulk Upload Product 2',
              baseUOM: '',  // 錯誤：空單位
              currentStock: -10, // 錯誤：負庫存
              reorderLevel: 'abc', // 錯誤：非數字
              weight: 'invalid', // 錯誤：非數字重量
              length: 10,
              width: 8,
              height: 3,
              sellingPrice: 59.99
            },
            {
              merchantReference: 'MERCH-UPLOAD-003',
              sku: 'SKU-001', // 錯誤：重複 SKU ID（與現有產品衝突）
              productName: '', // 錯誤：空產品名稱
              productNameEnglish: 'Bulk Upload Product 3',
              baseUOM: 'KG',
              currentStock: 50,
              reorderLevel: 10,
              weight: 2.0,
              length: 15,
              width: 12,
              height: 5,
              sellingPrice: -25.00 // 錯誤：負價格
            }
          ];
          
          // 驗證每個記錄的每個字段
          const allErrors: Array<{row: number; field: string; message: string; value: any}> = [];
          
          mockParsedData.forEach((record, index) => {
            const rowNumber = index + 2; // +2 因為第1行是標題，數據從第2行開始
            
            // 驗證每個必需字段
            Object.keys(record).forEach(field => {
              const validation = validateProductField(field, (record as any)[field], rowNumber);
              if (!validation.isValid) {
                allErrors.push({
                  row: rowNumber,
                  field: field,
                  message: validation.message!,
                  value: (record as any)[field]
                });
              }
            });
          });
          
          resolve({
            data: mockParsedData,
            errors: allErrors
          });
          
        } catch (error) {
          resolve({
            data: [],
            errors: [{row: 0, field: 'file', message: t('文件解析失敗，請檢查文件格式'), value: ''}]
          });
        }
      };
      
      reader.readAsText(file);
    });
  };

  // 處理批量上傳
  const handleBatchUpload = async (file: File) => {
    try {
      setUploadStatus({
        isProcessing: true,
        data: [],
        errors: [],
        hasErrors: false
      });
      
      // 模擬文件處理延遲
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // 解析和驗證文件
      const result = await processBatchUploadFile(file);
      
      const hasErrors = result.errors.length > 0;
      
      setUploadStatus({
        isProcessing: false,
        data: result.data,
        errors: result.errors,
        hasErrors: hasErrors
      });
      
      if (hasErrors) {
        message.error(t('發現 {{count}} 個錯誤，請修正後重新上傳', { count: result.errors.length }));
      } else {
        message.success(t('文件驗證通過，可以進行上傳'));
      }
      
    } catch (error) {
      setUploadStatus({
        isProcessing: false,
        data: [],
        errors: [{row: 0, field: 'system', message: t('系統錯誤：無法處理文件'), value: ''}],
        hasErrors: true
      });
      message.error(t('批量上傳處理失敗'));
    }
  };

  // 執行實際的批量上傳（只有在沒有錯誤時才允許）
  const executeBatchUpload = async () => {
    if (uploadStatus.hasErrors || uploadStatus.errors.length > 0) {
      message.error(t('存在驗證錯誤，無法上傳。請修正所有錯誤後重新上傳文件。'));
      return;
    }
    
    if (uploadStatus.data.length === 0) {
      message.error(t('沒有有效的數據可以上傳'));
      return;
    }
    
    try {
      setLoading(true);
      
      // 模擬上傳過程
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // 將驗證通過的數據轉換為產品格式
      const newProducts = uploadStatus.data.map(record => ({
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        merchantReference: record.merchantReference,
        sku: record.sku,
        internalReference: generateInternalReference(),
        barcode: record.barcode || '',
        productName: record.productName,
        productNameEnglish: record.productNameEnglish,
        productNameChinese: record.productNameChinese || record.productName,
        description: record.description || '',
        brand: record.brand || '',
        length: Number(record.length) || 0,
        width: Number(record.width) || 0,
        height: Number(record.height) || 0,
        weight: Number(record.weight) || 0,
        // 包裝尺寸字段
        packagingLength: Number(record.packagingLength) || 0,
        packagingWidth: Number(record.packagingWidth) || 0,
        packagingHeight: Number(record.packagingHeight) || 0,
        packagingWeight: Number(record.packagingWeight) || 0,
        packageCBM: (Number(record.packagingLength) && Number(record.packagingWidth) && Number(record.packagingHeight)) 
          ? (Number(record.packagingLength) * Number(record.packagingWidth) * Number(record.packagingHeight)) / 1000000 
          : 0,
        unitCBM: (Number(record.packagingLength) && Number(record.packagingWidth) && Number(record.packagingHeight) && Number(record.conversionFactor)) 
          ? ((Number(record.packagingLength) * Number(record.packagingWidth) * Number(record.packagingHeight)) / 1000000) / Number(record.conversionFactor)
          : 0,
        temperatureRequirement: record.temperatureRequirement || undefined,
        baseUOM: record.baseUOM,
        packagingUOM: record.packagingUOM || record.baseUOM,
        conversionFactor: Number(record.conversionFactor) || 1,
        lotNumber: record.lotNumber || '',
        serialNumber: record.serialNumber || '',
        batchControlType: BatchControlType.FIFO,
        lotNumberRequired: false,
        serialNumberRequired: false,
        currentStock: Number(record.currentStock) || 0,
        reorderLevel: Number(record.reorderLevel) || 0,
        safetyStock: Number(record.safetyStock) || 0,
        maxStock: Number(record.maxStock) || 1000,
        sellingPrice: Number(record.sellingPrice) || 0,
        isBundle: false,
        multiStoreEnabled: true,
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        alertEmails: ['admin@company.com'],
        alertEnabled: true,
        productAttributes: [],
        supplier: record.supplier || 'Default Supplier',
        category: record.category ? [record.category] : [t('未分類')]
      }));
      
      setProducts([...products, ...newProducts]);
      
      // 清空上傳狀態
      setUploadStatus({
        isProcessing: false,
        data: [],
        errors: [],
        hasErrors: false
      });
      
      message.success(t('成功上傳 {{count}} 個產品', { count: newProducts.length }));
      
    } catch (error) {
      message.error(t('批量上傳失敗'));
    } finally {
      setLoading(false);
    }
  };



  // KPI點擊處理
  const handleKPIClick = (type: keyof KPIStats) => {
    let filteredData: ProductMaster[] = [];
    let title = '';

    switch (type) {
      case 'totalProducts':
        filteredData = products;
        title = t('總產品詳情');
        break;
      case 'totalValue':
        filteredData = [...products].sort((a, b) => (b.currentStock * 1000) - (a.currentStock * 1000));
        title = t('總價值詳情（按價值排序）');
        break;
      case 'lowStockItems':
        filteredData = products.filter(p => p.currentStock <= p.reorderLevel);
        title = t('低庫存項目詳情');
        break;
      case 'outOfStockItems':
        filteredData = products.filter(p => p.currentStock === 0);
        title = t('缺貨項目詳情');
        break;
    }

    // 顯示詳情模態框
    Modal.info({
      title,
      width: 1000,
      content: (
        <div>
          <Table
            dataSource={filteredData}
            columns={[
              {
                title: t('產品名稱'),
                dataIndex: 'productName',
                key: 'productName',
              },
              {
                title: t('SKU ID'),
                dataIndex: 'sku',
                key: 'sku',
              },
              {
                title: t('當前庫存'),
                dataIndex: 'currentStock',
                key: 'currentStock',
              },
              {
                title: t('重新訂購水平'),
                dataIndex: 'reorderLevel',
                key: 'reorderLevel',
              },
              {
                title: t('狀態'),
                key: 'status',
                render: (_, record) => {
                  if (record.currentStock === 0) {
                    return <Tag color="red">{t('缺貨')}</Tag>;
                  } else if (record.currentStock <= record.reorderLevel) {
                    return <Tag color="orange">{t('低庫存')}</Tag>;
                  } else {
                    return <Tag color="green">{t('正常')}</Tag>;
                  }
                },
              },
            ]}
            pagination={false}
            size="small"
          />
        </div>
      ),
    });
  };

  // 定義所有可用的產品列
  const allProductColumns = {
    product: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleProductColumnSort('productName')}
        >
          {t('產品信息')}
                          {getProductSortIcon('productName')}
        </div>
      ),
      key: 'product',
      width: 280,
      sorter: true,
      render: (_: any, record: any) => (
        <div>
          <Space size={4}>
            <Text strong>{record.productName}</Text>
            {record.isChildSku && record.parentSkuInfo && (
              <Tooltip title={t('子 SKU：庫存來自父 SKU「{{code}}」({{name}})', { code: record.parentSkuInfo.parentItemCode, name: record.parentSkuInfo.parentItemName })}>
                <LinkOutlined style={{ color: '#1890ff', fontSize: 14 }} />
              </Tooltip>
            )}
          </Space>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {t('SKU ID:')} {record.sku}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            <BarcodeOutlined /> {record.barcode}
          </Text>
        </div>
      ),
    },
    // 拆分列：產品名稱
    productName: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleProductColumnSort('productName')}
        >
          {t('產品名稱')}
                          {getProductSortIcon('productName')}
        </div>
      ),
      key: 'productName',
      width: 180,
      sorter: false,
      render: (_: any, record: any) => (
        <Space size={4}>
          <Text strong>{record.productName}</Text>
          {record.isChildSku && record.parentSkuInfo && (
            <Tooltip title={t('子 SKU：庫存來自父 SKU「{{code}}」({{name}})', { code: record.parentSkuInfo.parentItemCode, name: record.parentSkuInfo.parentItemName })}>
              <LinkOutlined style={{ color: '#1890ff', fontSize: 14 }} />
            </Tooltip>
          )}
        </Space>
      )
    },
    // 拆分列：SKU ID
    sku: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleProductColumnSort('sku')}
        >
          {t('SKU ID')}
                          {getProductSortIcon('sku')}
        </div>
      ),
      key: 'sku',
      width: 120,
      sorter: false,
      render: (_: any, record: any) => <Text type="secondary" style={{ fontSize: '12px' }}>{t('SKU ID:')} {record.sku}</Text>
    },
    // 拆分列：條碼
    barcode: {
      title: t('條形碼'),
      key: 'barcode',
      width: 160,
      render: (_: any, record: any) => <Text type="secondary" style={{ fontSize: '12px' }}><BarcodeOutlined /> {record.barcode}</Text>
    },
    references: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleProductColumnSort('merchantReference')}
        >
          {t('參考信息')}
                          {getProductSortIcon('merchantReference')}
        </div>
      ),
      key: 'references',
      width: 200,
      sorter: true,
      render: (_: any, record: any) => (
        <div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {t('商家:')} {record.merchantReference}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {t('內部:')} {record.internalReference}
          </Text>
        </div>
      ),
    },
    // 拆分列：商家參考
    merchantReference: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleProductColumnSort('merchantReference')}
        >
          {t('商家參考')}
                          {getProductSortIcon('merchantReference')}
        </div>
      ),
      key: 'merchantReference',
      width: 160,
      sorter: false,
      render: (_: any, record: any) => <Text type="secondary" style={{ fontSize: '12px' }}>{record.merchantReference}</Text>
    },
    // 拆分列：內部參考
    internalReference: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleProductColumnSort('internalReference')}
        >
          {t('內部參考')}
                          {getProductSortIcon('internalReference')}
        </div>
      ),
      key: 'internalReference',
      width: 160,
      sorter: false,
      render: (_: any, record: any) => <Text type="secondary" style={{ fontSize: '12px' }}>{record.internalReference}</Text>
    },
    stock: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleProductColumnSort('currentStock')}
        >
          {t('庫存狀態')}
                          {getProductSortIcon('currentStock')}
        </div>
      ),
      key: 'stock',
      width: 140,
      sorter: true,
      render: (_: any, record: any) => (
        <div>
          <Text strong>{record.currentStock}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {t('重新訂購:')} {record.reorderLevel}
          </Text>
        </div>
      ),
    },
    // 拆分列：當前庫存
    currentStock: {
      title: t('當前庫存'),
      key: 'currentStock',
      width: 120,
      sorter: false,
      render: (_: any, record: any) => <Text strong>{record.currentStock}</Text>
    },
    // 拆分列：重新訂購水平
    reorderLevel: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleProductColumnSort('reorderLevel')}
        >
          {t('重新訂購')}
                          {getProductSortIcon('reorderLevel')}
        </div>
      ),
      key: 'reorderLevel',
      width: 120,
      sorter: false,
      render: (_: any, record: any) => <Text type="secondary" style={{ fontSize: '12px' }}>{record.reorderLevel}</Text>
    },
    dimensions: {
      title: t('尺寸信息'),
      key: 'dimensions',
      width: 150,
      render: (_: any, record: any) => (
        <div>
          {record.length && record.width && record.height ? (
            <>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {record.length}×{record.width}×{record.height}cm
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {t('CBM:')} {record.packageCBM?.toFixed(6)}m³
              </Text>
            </>
          ) : (
            <Text type="secondary">{t('N/A')}</Text>
          )}
        </div>
      ),
    },
    // 拆分列：尺寸
    size: {
      title: t('尺寸'),
      key: 'size',
      width: 140,
      render: (_: any, record: any) => (
        record.length && record.width && record.height ? (
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.length}×{record.width}×{record.height}cm
          </Text>
        ) : <Text type="secondary">{t('N/A')}</Text>
      )
    },
    // 拆分列：CBM
    cbm: {
      title: 'CBM',
      key: 'cbm',
      width: 120,
      render: (_: any, record: any) => (
        (record.packageCBM !== undefined && record.packageCBM !== null)
          ? <Text type="secondary" style={{ fontSize: '12px' }}>{Number(record.packageCBM).toFixed(6)}m³</Text>
          : <Text type="secondary">{t('N/A')}</Text>
      )
    },
    units: {
      title: t('單位信息'),
      key: 'units',
      width: 120,
      render: (_: any, record: any) => (
        <div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {t('基本:')} {record.baseUOM}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {t('包裝:')} {record.packagingUOM}
          </Text>
        </div>
      ),
    },
    // 拆分列：基本單位
    baseUOM: {
      title: t('基本單位'),
      key: 'baseUOM',
      width: 100,
      render: (_: any, record: any) => <Text type="secondary" style={{ fontSize: '12px' }}>{record.baseUOM || t('N/A')}</Text>
    },
    // 拆分列：包裝單位
    packagingUOM: {
      title: t('包裝單位'),
      key: 'packagingUOM',
      width: 100,
      render: (_: any, record: any) => <Text type="secondary" style={{ fontSize: '12px' }}>{record.packagingUOM || t('N/A')}</Text>
    },
    supplier: {
      title: (
        <div 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleProductColumnSort('supplier')}
        >
          {t('供應商')}
                          {getProductSortIcon('supplier')}
        </div>
      ),
      key: 'supplier',
      width: 160,
      sorter: false,
      render: (_: any, record: any) => (
        <div>
          {record.supplier ? (
            <Text style={{ fontSize: '12px' }}>
              {record.supplier}
            </Text>
          ) : (
            <Text type="secondary">{t('未設定')}</Text>
          )}
        </div>
      ),
    },
    category: {
      title: t('產品分類'),
      key: 'category',
      width: 180,
      render: (_: any, record: any) => (
        <div>
          {record.category && record.category.length > 0 ? (
            <Space wrap size="small">
                              {record.category.slice(0, 2).map((cat: any, index: any) => (
                <Tag key={index} color="blue" style={{ fontSize: '11px', margin: '1px' }}>
                  {cat}
                </Tag>
              ))}
              {record.category.length > 2 && (
                <Tag color="default" style={{ fontSize: '11px' }}>
                  +{record.category.length - 2}
                </Tag>
              )}
            </Space>
          ) : (
            <Text type="secondary">{t('未分類')}</Text>
          )}
        </div>
      ),
    },
    customer: {
      title: t('客戶'),
      key: 'customer',
      width: 150,
      render: (_: any, record: any) => (
        <div>
          {record.customerNames ? (
            <Text style={{ fontSize: '12px' }}>{record.customerNames}</Text>
          ) : (
            <Text type="secondary">{t('未指定')}</Text>
          )}
        </div>
      ),
    },
    batchControl: {
      title: t('批次控制'),
      key: 'batchControl',
      width: 160,
      render: (_: any, record: any) => {
        const configs: any[] = (Array.isArray((window as any).__dynamicBatchConfigs) && (window as any).__dynamicBatchConfigs.length
          ? (window as any).__dynamicBatchConfigs
          : batchControlConfigs);
        const config = configs.find(c => c.type === record.batchControlType);
        return (
          <div>
            <Tag color="cyan" style={{ fontSize: '11px', marginBottom: 4 }}>
              {config?.label.split(' ')[0] || record.batchControlType}
            </Tag>
            <br />
            <Space size="small">
              {record.lotNumberRequired && (
                <Tag color="green">{t('批次')}</Tag>
              )}
              {record.serialNumberRequired && (
                <Tag color="blue">{t('序號')}</Tag>
              )}
            </Space>
            <br />
            <Button
              type="link"
              size="small"
              onClick={() => testBatchControl(record)}
              style={{ padding: 0, fontSize: '11px' }}
            >
              {t('測試排序')}
                                </Button>
          </div>
        );
      },
    },
    status: {
      title: t('狀態'),
      key: 'status',
      width: 140,
      render: (_: any, record: any) => (
        <div>
          <Badge
            status={record.currentStock <= record.reorderLevel ? 'error' : 'success'}
            text={record.currentStock <= record.reorderLevel ? t('低庫存') : t('正常')}
          />
          <br />
          <Space size="small">
            {record.isActive && <Tag color="green">{t('活躍')}</Tag>}
            {record.isBundle && <Tag color="purple">{t('捆綁')}</Tag>}
            {record.multiStoreEnabled && <Tag color="blue">{t('多商店')}</Tag>}
            {record.alertEnabled && <Tag color="orange">{t('警報')}</Tag>}
          </Space>
        </div>
      ),
    },
    actions: {
      title: t('操作'),
      key: 'actions',
      width: isCustomerRole ? 120 : 220,
      fixed: 'right' as 'right',
      render: (_: any, record: any) => (
        <Space>
          <Tooltip title={t('查看詳情')}>
            <Button
              type="default"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
            >
              {t('詳情')}
                                  </Button>
          </Tooltip>
          {!isCustomerRole && (
            <>
              <Tooltip title={t('編輯')}>
                <Button
                  type="default"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                >
                  {t('編輯')}
                                              </Button>
              </Tooltip>
              <Popconfirm title={t('確認刪除此產品？')} okText={t('確認')} cancelText={t('取消')} onConfirm={() => handleDelete(record)}>
                <Button type="default" size="small" danger icon={<DeleteOutlined />}>{t('刪除')}</Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  };

  // 根據列配置生成顯示的列（與默認值合併，兼容舊配置）
  const aggregatedKeys = new Set(['product','references','stock','dimensions','units']);
  const getDefaultVisible = (key: string) => !aggregatedKeys.has(key);
  const defaultOrderMap: Record<string, number> = {
    productName: 0,
    sku: 1,
    barcode: 2,
    merchantReference: 3,
    internalReference: 4,
    currentStock: 5,
    reorderLevel: 6,
    size: 7,
    cbm: 8,
    baseUOM: 9,
    packagingUOM: 10,
    supplier: 11,
    category: 12,
    batchControl: 13,
    status: 14,
    actions: 15,
    product: 100,
    references: 101,
    stock: 102,
    dimensions: 103,
    units: 104
  };
  // 菜單顯示名稱映射，避免多層三元判斷
  const columnTitleMap: Record<string, string> = {
    productName: t('產品名稱'),
    sku: 'SKU ID',
    barcode: t('條形碼'),
    merchantReference: t('商家參考'),
    internalReference: t('內部參考'),
    currentStock: t('當前庫存'),
    reorderLevel: t('重新訂購'),
    size: t('尺寸'),
    cbm: 'CBM',
    baseUOM: t('基本單位'),
    packagingUOM: t('包裝單位'),
    supplier: t('供應商'),
    category: t('產品分類'),
    batchControl: t('批次控制'),
    status: t('狀態'),
    actions: t('操作'),
    product: t('產品信息'),
    references: t('參考信息'),
    stock: t('庫存狀態'),
    dimensions: t('尺寸信息'),
    units: t('單位信息'),
  };
  const columns: ColumnsType<ProductMaster> = Object.entries(allProductColumns)
    .map(([key, col]) => {
      const setting = (productColumnSettings as any)[key] || { visible: getDefaultVisible(key), order: defaultOrderMap[key] ?? 999 };
      const enforcedVisible = aggregatedKeys.has(key) ? false : (setting.visible ?? getDefaultVisible(key));
      return { key, col, visible: enforcedVisible, order: setting.order };
    })
    .filter(item => item.visible)
    .sort((a, b) => a.order - b.order)
    .map(item => item.col) as any;

  // 處理產品列可見性切換
  const handleProductColumnVisibilityChange = (columnKey: string, visible: boolean) => {
    const existing = productColumnSettings[columnKey as keyof typeof productColumnSettings] as any;
    const defaultOrder = Object.keys(productColumnSettings).length + 1;
    const newSettings = {
      ...productColumnSettings,
      [columnKey]: { ...(existing || { order: defaultOrder }), visible }
    } as any;
    setProductColumnSettings(newSettings);
    localStorage.setItem('product-master-columns-config', JSON.stringify(newSettings));
    localStorage.setItem('product-master-columns-version', '2025-01-v3');
  };

  // 顯示所有產品列
  const showAllProductColumns = () => {
    const allVisibleSettings = {
      productName: { visible: true, order: 0 },
      sku: { visible: true, order: 1 },
      barcode: { visible: true, order: 2 },
      merchantReference: { visible: true, order: 3 },
      internalReference: { visible: true, order: 4 },
      totalStock: { visible: true, order: 5 },
      size: { visible: true, order: 6 },
      cbm: { visible: true, order: 7 },
      baseUOM: { visible: true, order: 8 },
      packagingUOM: { visible: true, order: 9 },
      supplier: { visible: true, order: 10 },
      category: { visible: true, order: 11 },
      customer: { visible: true, order: 12 },
      batchControl: { visible: true, order: 13 },
      status: { visible: true, order: 14 },
      actions: { visible: true, order: 15 },
      // Keep aggregated columns hidden
      product: { visible: false, order: 100 },
      references: { visible: false, order: 101 },
      stock: { visible: false, order: 102 },
      dimensions: { visible: false, order: 103 },
      units: { visible: false, order: 104 }
    } as any;
    setProductColumnSettings(allVisibleSettings);
    localStorage.setItem('product-master-columns-config', JSON.stringify(allVisibleSettings));
    localStorage.setItem('product-master-columns-version', '2025-01-v3');
    message.success(t('所有列已顯示'));
  };

  // 重置產品列配置
  const resetProductColumnSettings = () => {
    const defaultSettings = {
      productName: { visible: true, order: 0 },
      sku: { visible: true, order: 1 },
      barcode: { visible: true, order: 2 },
      merchantReference: { visible: true, order: 3 },
      internalReference: { visible: true, order: 4 },
      totalStock: { visible: true, order: 5 },
      size: { visible: true, order: 6 },
      cbm: { visible: true, order: 7 },
      baseUOM: { visible: true, order: 8 },
      packagingUOM: { visible: true, order: 9 },
      supplier: { visible: true, order: 10 },
      category: { visible: true, order: 11 },
      customer: { visible: true, order: 12 },
      batchControl: { visible: true, order: 13 },
      status: { visible: true, order: 14 },
      actions: { visible: true, order: 15 },
      product: { visible: false, order: 100 },
      references: { visible: false, order: 101 },
      stock: { visible: false, order: 102 },
      dimensions: { visible: false, order: 103 },
      units: { visible: false, order: 104 }
    } as any;
    setProductColumnSettings(defaultSettings);
    localStorage.setItem('product-master-columns-config', JSON.stringify(defaultSettings));
    localStorage.setItem('product-master-columns-version', '2025-01-v3');
    message.success(t('列配置已重置'));
  };

  // 產品列設置菜單
  const productColumnSettingsMenu = (
    <div style={{ 
      background: '#fff', 
      borderRadius: '2px', 
      boxShadow: '0 3px 6px -4px rgba(0,0,0,.12), 0 6px 16px 0 rgba(0,0,0,.08), 0 9px 28px 8px rgba(0,0,0,.05)',
      padding: '4px 0',
      minWidth: '160px'
    }}>
      <div style={{ padding: '5px 12px', borderBottom: '1px solid #f0f0f0' }}>
        <Text strong>{t('列設置')}</Text>
      </div>
      <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '4px 0' }}>
        {Object.entries(allProductColumns)
          .filter(([key]) => !aggregatedKeys.has(key))
          .sort(([a], [b]) => (defaultOrderMap[a] ?? 999) - (defaultOrderMap[b] ?? 999))
          .map(([key, column]) => (
          <div 
            key={key}
            style={{ padding: '5px 12px', cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              handleProductColumnVisibilityChange(key, !((productColumnSettings as any)[key]?.visible ?? getDefaultVisible(key)));
            }}
          >
            <Checkbox
              checked={(productColumnSettings as any)[key]?.visible ?? getDefaultVisible(key)}
              onChange={(e) => {
                e.stopPropagation();
                handleProductColumnVisibilityChange(key, e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {typeof column.title === 'string' ? column.title : (columnTitleMap[key] || key)}
            </Checkbox>
          </div>
        ))}
      </div>
      <div style={{ borderTop: '1px solid #f0f0f0', padding: '4px 0' }}>
        <div 
          style={{ padding: '5px 12px', cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            showAllProductColumns();
          }}
        >
          <Button type="link" size="small" style={{ padding: 0 }}>
            {t('顯示所有列')}
                                </Button>
        </div>
        <div 
          style={{ padding: '5px 12px', cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            resetProductColumnSettings();
          }}
        >
          <Button type="link" size="small" style={{ padding: 0 }}>
            {t('重置列設置')}
                                </Button>
        </div>
      </div>
    </div>
  );

  // 處理產品數據排序
  const sortedProducts = products; // 排序改由後端處理

  useEffect(() => {
    // Load products on mount - productSearchFilter is already initialized with URL param if present
    loadProducts();
    // Load customers for filter dropdown
    loadCustomersWithProducts();
    // 動態加載批次策略列表（public.batch_strategies）
    (async () => {
      try {
        let resp: any = await apiService.getBatchStrategies().catch(() => null);
        if (!resp?.strategies || resp.strategies.length === 0) {
          await apiService.seedBatchStrategies().catch(() => null);
          resp = await apiService.getBatchStrategies().catch(() => null);
        }
        if (resp?.strategies?.length) {
          const mapped = (resp.strategies as any[])
            .filter(s => s.is_active !== false)
            .sort((a,b) => (a.display_order||0)-(b.display_order||0))
            .map(s => ({
              type: s.code,
              label: s.title || String(s.code || '').toUpperCase(),
              explanation: s.description || '',
              useCases: [] as string[],
            }));
          // 將動態策略覆寫本地默認配置（若你保留本地，亦可合併）
          // 這裡使用 window 級變量保存，避免大改現有結構
          (window as any).__dynamicBatchConfigs = mapped;
        }
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    calculateKPIStats();
  }, [products]);

  // 排序變更時，後端重載並回到第1頁
  useEffect(() => {
    setProductCurrentPage(1);
    loadProducts(1, productPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productSortField, productSortOrder]);

  // 在切換內層 Tab（如高級設置）時，強制同步三個開關與郵件，避免初始值覆蓋
  const handleInnerTabChange = (key: string) => {
    if (key === 'advanced') {
      const p: any = lastDetailRef.current || {};
      const current = form.getFieldsValue();
      form.setFieldsValue({
        isActive: (p.isActive !== undefined ? !!p.isActive : !!current.isActive),
        alertEnabled: (p.alertEnabled !== undefined ? !!p.alertEnabled : !!current.alertEnabled),
        multiStoreEnabled: (p.multiStoreEnabled !== undefined ? !!p.multiStoreEnabled : !!current.multiStoreEnabled),
        alertEmails: Array.isArray(p.alertEmails) ? p.alertEmails : (current.alertEmails || []),
      });
    }
  };

  // 搜尋可作為備用 SKU 的產品
  const handleAlternateSkuSearch = async (searchValue: string) => {
    console.log('[AlternateSKU] handleAlternateSkuSearch called with:', searchValue);
    if (!searchValue || searchValue.trim().length < 2) {
      console.log('[AlternateSKU] Search value too short, clearing results');
      setAlternateSkuSearchResults([]);
      return;
    }
    try {
      console.log('[AlternateSKU] Calling API with search:', searchValue.trim(), 'excludeProductId:', editingProduct?.id);
      const res = await apiService.getEligibleAlternateSkus(searchValue.trim(), editingProduct?.id) as { products?: Array<{id: string; itemCode: string; itemName: string}> };
      console.log('[AlternateSKU] API response:', res);
      setAlternateSkuSearchResults(res.products || []);
      console.log('[AlternateSKU] Set results:', res.products?.length || 0, 'products');
    } catch (e) {
      console.error('[AlternateSKU] Failed to search eligible alternate SKUs:', e);
      setAlternateSkuSearchResults([]);
    }
  };

  // 添加備用 SKU（從下拉選單選擇）
  function addAlternateSkuFromSelect(itemCode: string) {
    if (!itemCode) return;

    // 檢查是否與主SKU相同
    const mainSku = form.getFieldValue('sku');
    if (itemCode === mainSku) {
      message.error(t('備用SKU不能與主要SKU相同'));
      return;
    }

    // 檢查是否已存在於備用SKU列表中
    if (alternateSkuList.includes(itemCode)) {
      message.error(t('備用SKU已存在'));
      return;
    }

    setAlternateSkuList(prev => [...prev, itemCode]);
    form.setFieldsValue({ alternateSkuInput: undefined });
    setAlternateSkuSearchResults([]);
    message.success(t('已添加備用 SKU: {{itemCode}}', { itemCode }));
  }

  function addAlternateSku() {
    const value = form.getFieldValue('alternateSkuInput');
    if (value) {
      // 檢查是否與主SKU相同
      const mainSku = form.getFieldValue('sku');
      if (value === mainSku) {
        message.error(t('備用SKU不能與主要SKU相同'));
        return;
      }

      // 檢查是否已存在於備用SKU列表中
      if (alternateSkuList.includes(value)) {
        message.error(t('備用SKU已存在'));
        return;
      }

      setAlternateSkuList(prev => [...prev, value]);
      form.setFieldsValue({ alternateSkuInput: '' });
    }
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <Title level={2} style={{ marginBottom: 0 }}>{t('產品主設置')}</Title>
        <Text type="secondary">{t('管理產品信息、SKU ID、庫存和警報設置')}</Text>
        </div>
        <Button
          type="text"
          icon={collapsed ? <DownOutlined /> : <UpOutlined />}
          onClick={toggleCollapsed}
          style={{ fontSize: 16 }}
        />
      </div>

      {/* KPI 儀表板 */}
      {!collapsed && (
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={6}>
          <Card hoverable onClick={() => handleKPIClick('totalProducts')}>
            <Statistic
              title={t('總產品數')}
              value={kpiStats.totalProducts}
              prefix={<AppstoreOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card hoverable onClick={() => handleKPIClick('totalValue')}>
            <Statistic
              title={t('總價值')}
              value={kpiStats.totalValue}
              prefix="$"
              suffix="K"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card hoverable onClick={() => handleKPIClick('lowStockItems')}>
            <Statistic
              title={t('低庫存項目')}
              value={kpiStats.lowStockItems}
              prefix={<AlertOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card hoverable onClick={() => handleKPIClick('outOfStockItems')}>
            <Statistic
              title={t('缺貨項目')}
              value={kpiStats.outOfStockItems}
              prefix={<BellOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>
      )}

      {/* 主要內容 - 使用Tabs */}
      <Tabs defaultActiveKey="overview">
        <TabPane 
          tab={
            <span>
              <AppstoreOutlined />
              {t('產品概覽')}
                                      </span>
          } 
          key="overview"
        >
          {/* 操作按鈕（始終顯示，不受折疊影響） */}
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space>
              {!isCustomerRole && (
                <>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreate}
                  >
                    {t('創建產品')}
                                                        </Button>
                  <Button 
                    icon={<UploadOutlined />} 
                    onClick={() => setBulkUploadVisible(true)}
                  >
                    {t('批量上傳')}
                                                        </Button>
                </>
              )}

              <Button icon={<ReloadOutlined />} onClick={() => loadProducts()}>
                {t('刷新')}
                                            </Button>
              <Button 
                icon={<FileExcelOutlined />}
                onClick={handleExportExcel}
                type="default"
              >
                {t('導出')}
                                              </Button>
            </Space>
            <Space>
              {/* 客戶過濾下拉 */}
              <Select
                allowClear
                showSearch
                placeholder={t('選擇客戶')}
                value={selectedCustomerName || undefined}
                onChange={(value) => {
                  setSelectedCustomerName(value || '');
                  setProductCurrentPage(1);
                  loadProducts(1, productPageSize, undefined, value || '');
                }}
                style={{ width: 200 }}
                filterOption={(input, option) =>
                  (option?.children as unknown as string)?.toLowerCase()?.includes(input.toLowerCase())
                }
              >
                {customersWithProducts.map(c => (
                  <Option key={c.name} value={c.name}>{c.name}</Option>
                ))}
              </Select>
              {/* 產品搜尋（SKU/Barcode/Name 自動完成） */}
              <AutoComplete
                options={productSearchOptions}
                value={productSearchFilter}
                onChange={(value) => setProductSearchFilter(value)}
                onSearch={handleProductSearch}
                onSelect={(value) => {
                  setProductSearchFilter(value);
                  setProductCurrentPage(1);
                  loadProducts(1, productPageSize, value);
                }}
                style={{ width: 350 }}
                placeholder={t('輸入 SKU / Barcode / 產品名稱搜尋')}
              >
                <Input.Search
                  prefix={<BarcodeOutlined />}
                  onSearch={() => {
                    setProductCurrentPage(1);
                    loadProducts(1, productPageSize);
                  }}
                  enterButton
                />
              </AutoComplete>
              {/* 已移除頁面縮放控制 */}
              <Dropdown 
                overlay={productColumnSettingsMenu} 
                trigger={['click']}
                open={productColumnSettingsVisible}
                onOpenChange={setProductColumnSettingsVisible}
              >
                <Button icon={<SettingOutlined />} title={t('列設置')}>
                  {t('列設置')}
                                                  </Button>
              </Dropdown>
            </Space>
          </div>

          {/* 產品表格 */}
          <Card>
            <Table
              columns={columns}
              dataSource={sortedProducts}
              loading={loading}
              rowKey="id"
              scroll={{ x: 1600, y: 600 }}
              pagination={{
                total: productTotalCount,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: [10, 20, 50, 100, 500],
                pageSize: productPageSize,
                current: productCurrentPage,
                showTotal: (total, range) => t('第 {{from}}-{{to}} 條，共 {{total}} 條', { from: range[0], to: range[1], total }),
              }}
              onChange={(pagination) => {
                const newSize = pagination.pageSize || productPageSize;
                const newPage = pagination.current || 1;
                if (newSize !== productPageSize) {
                  setProductPageSize(newSize);
                  setProductCurrentPage(1);
                  loadProducts(1, newSize);
                } else {
                  setProductCurrentPage(newPage);
                  loadProducts(newPage, productPageSize);
                }
              }}
            />
          </Card>
        </TabPane>


      </Tabs>

      {/* 產品創建/編輯模態框 */}
      <Modal
        title={modalTitle}
        open={modalVisible}
        onOk={isReadOnly ? undefined : handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={800}
        destroyOnClose
        footer={isReadOnly ? null : undefined}
      >
        {modalVisible && (
          <>
            {/* 必填字段提示 */}
            <Alert
              message={t('填寫產品基本信息')}
              description={
                <div>
                  <Text strong>{t('必填字段包括：')}</Text>
                  <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                    <li>{t('SKU ID、條形碼、產品名稱')}</li>
                    <li>{t('基本單位、包裝單位、轉換因子')}</li>
                    <li>{t('當前庫存、最大庫存')}</li>
                    <li>{t('批次控制策略')}</li>
                  </ul>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {t('* 標有紅色星號的字段為必填項目')}
                                            </Text>
                </div>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Form
              form={form}
              layout="vertical"
              disabled={isReadOnly}
              onValuesChange={handleValuesChange}
              initialValues={{
                isActive: true,
                alertEnabled: true,
                alertEmails: ['admin@company.com'],
                batchControlType: BatchControlType.FIFO,
                lotNumberRequired: false,
                serialNumberRequired: false,
                conversionFactor: 1,
                currentStock: 0,
                reorderLevel: 0,
                safetyStock: 0,
                maxStock: 1000,
                isBundle: false,
                bundleItems: [],
                isPackaged: true,
              }}
            >
            <Tabs defaultActiveKey="basic" onChange={handleInnerTabChange} destroyInactiveTabPane={false}>
              <TabPane tab={t('基本信息')} key="basic">
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="merchantReference"
                      label={t('商家參考')}
                      rules={[{ required: false }]}
                    >
                      <Input placeholder={t('輸入商家參考（可選）')} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="sku"
                      label={t('SKU ID')}
                      rules={[{ required: true, message: t('請輸入SKU ID') }]}
                      tooltip={editingProduct ? t('SKU ID 在編輯模式下不可更改') : undefined}
                    >
                      <Input
                        placeholder={t('輸入SKU ID')}
                        disabled={!!editingProduct}
                      />
                    </Form.Item>
                    
                    {/* 副SKU輸入區域 */}
                    {skuValue && skuValue.trim() !== '' && (
                      <>
                        {/* 子 SKU 警告訊息 */}
                        {editingIsChildSku && editingParentSkuInfo && (
                          <div style={{ marginBottom: 12, padding: '8px 12px', backgroundColor: '#fff7e6', border: '1px solid #ffd591', borderRadius: 4 }}>
                            <Text type="warning">
                              <LinkOutlined style={{ marginRight: 6 }} />
                              {t('此產品是子 SKU，庫存來自父產品「{{code}}」({{name}})。 子 SKU 不能添加備用 SKU。', { code: editingParentSkuInfo.parentItemCode, name: editingParentSkuInfo.parentItemName })}
                                                                                          </Text>
                          </div>
                        )}

                        <Form.Item
                          label={t('備用SKU')}
                          name="alternateSkuInput"
                          extra={editingIsChildSku ? t('子 SKU 不能添加備用 SKU') : t('搜尋並選擇要作為備用 SKU 的產品（輸入至少2個字元）')}
                          hidden={isReadOnly}
                        >
                          <Select
                            showSearch
                            allowClear
                            placeholder={editingIsChildSku ? t('子 SKU 不能添加備用 SKU') : t('搜尋 SKU 或產品名稱...')}
                            filterOption={false}
                            onSearch={handleAlternateSkuSearch}
                            onSelect={(value: string) => addAlternateSkuFromSelect(value)}
                            onClear={() => setAlternateSkuSearchResults([])}
                            notFoundContent={alternateSkuSearchResults.length === 0 ? t('輸入至少2個字元搜尋') : null}
                            disabled={isReadOnly || editingIsChildSku}
                            value={undefined}
                          >
                            {alternateSkuSearchResults.map(p => (
                              <Option key={p.itemCode} value={p.itemCode}>
                                <Space>
                                  <Text strong>{p.itemCode}</Text>
                                  <Text type="secondary">-</Text>
                                  <Text>{p.itemName}</Text>
                                </Space>
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>

                        {/* 顯示已添加的備用SKU */}
                        <div style={{ marginTop: 4 }}>
                          <Space size={[8, 8]} wrap>
                            {alternateSkuList.map((code, index) => (
                              <Tag key={index} closable={!isReadOnly && !editingIsChildSku} onClose={() => {
                                setAlternateSkuList(prev => prev.filter(item => item !== code));
                              }}>
                                {code}
                              </Tag>
                            ))}
                          </Space>
                        </div>
                      </>
                    )}
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="internalReference"
                      label={t('內部參考')}
                      tooltip={t('公司內部使用的料號/對應碼，與外部 SKU 不同；可手動輸入或點擊生成')}
                      rules={[
                        { required: false },
                        { max: 64, message: t('最長 64 字元') },
                        { pattern: /^[A-Za-z0-9\-_.\/]*$/, message: t('僅限字母、數字與 - _ . /') },
                      ]}
                    >
                      <Input 
                        allowClear
                        placeholder={t('輸入內部參考')}
                        addonAfter={
                          <Button
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={handleCopyInternalReference}
                            title={t('複製內部參考')}
                          >
                            {t('複製')}
                                                          </Button>
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    {/* hidden 實際提交欄位（逗號字串） */}
                    <Form.Item name="barcode" hidden>
                      <Input />
                    </Form.Item>
                    <Form.Item
                      label={t('條形碼')}
                      name="barcodeInput"
                      rules={[
                        {
                          validator: async () => {
                            if ((barcodeList || []).length > 0) return Promise.resolve();
                            return Promise.reject(new Error(t('請至少添加一個條碼')));
                          }
                        }
                      ]}
                    >
                      <Input
                        placeholder={t('掃描或輸入條形碼，按 Enter 追加')}
                        onPressEnter={(e) => {
                          e.preventDefault();
                          addBarcode();
                        }}
                        addonAfter={
                          <Space size={4}>
                            <Button type="text" icon={<BarcodeOutlined />} onClick={handleBarcodeScan}>
                              {t('掃描')}
                                                                </Button>
                            <Button type="text" onClick={() => addBarcode()}>{t('追加')}</Button>
                          </Space>
                        }
                      />
                    </Form.Item>
                    {barcodeList && barcodeList.length > 0 && (
                      <div style={{ marginTop: 4 }}>
                        <Space size={[8, 8]} wrap>
                          {barcodeList.map(code => (
                            <Tag key={code} closable onClose={() => removeBarcode(code)}>
                              <BarcodeOutlined style={{ marginRight: 4 }} />{code}
                            </Tag>
                          ))}
                        </Space>
                      </div>
                    )}
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="productName"
                      label={t('產品名稱')}
                      rules={[{ required: true, message: t('請輸入產品名稱') }]}
                    >
                      <Input placeholder={t('輸入產品名稱')} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="brand"
                      label={t('品牌')}
                    >
                      <Input placeholder={t('輸入品牌')} />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="description"
                  label={t('描述')}
                >
                  <Input.TextArea rows={3} placeholder={t('輸入產品描述')} />
                </Form.Item>

                <Form.Item
                  name="pickingRemark"
                  label={t('揀貨備註')}
                  extra={t('會以「Remark:」顯示在揀貨單的產品名稱下方')}
                >
                  <Input.TextArea rows={2} placeholder={t('輸入揀貨備註（列印於揀貨單）')} />
                </Form.Item>

                {/* 移除 Lot Number 與 Batch Number/序號 輸入欄位，避免與 SKU 混淆 */}

                <Divider orientation="left">{t('供應商和分類')}</Divider>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="supplier"
                      label={t('供應商')}
                      extra={t('來自採購模組的供應商主數據')}
                    >
                      <Select
                        style={{ width: '100%' }}
                        placeholder={t('選擇供應商')}
                        showSearch
                        filterOption={(input, option) =>
                          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={mockSuppliers}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="category"
                      label={t('產品分類')}
                      extra={t('可選擇現有分類或創建新分類標籤')}
                    >
                      <Select
                        mode="tags"
                        style={{ width: '100%' }}
                        placeholder={t('選擇或創建產品分類')}
                        tokenSeparators={[',']}
                        maxTagCount={5}
                        maxTagTextLength={15}
                      >
                        {mockCategories.map(category => (
                          <Option key={category} value={category}>
                            {category}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Divider orientation="left">{t('國際運輸/海關資料')}</Divider>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      name="hsCode"
                      label={t('海關編碼 (HS Code)')}
                      extra={t('國際運輸報關必填，6-10位數字')}
                      tooltip={t('Harmonized System Code 用於國際貿易分類，決定關稅稅率')}
                    >
                      <Input
                        placeholder={t('如：6109.10')}
                        maxLength={20}
                        disabled={isReadOnly}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="origin"
                      label={t('原產地 (Country of Origin)')}
                      extra={t('商品製造地/原產國')}
                      tooltip={t('商品的製造或原產國家/地區代碼')}
                    >
                      <Select
                        style={{ width: '100%' }}
                        placeholder={t('選擇原產地')}
                        showSearch
                        allowClear
                        filterOption={(input, option) =>
                          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        disabled={isReadOnly}
                        options={[
                          { value: 'CN', label: t('中國 (CN)') },
                          { value: 'HK', label: t('香港 (HK)') },
                          { value: 'TW', label: t('台灣 (TW)') },
                          { value: 'JP', label: t('日本 (JP)') },
                          { value: 'KR', label: t('韓國 (KR)') },
                          { value: 'US', label: t('美國 (US)') },
                          { value: 'GB', label: t('英國 (GB)') },
                          { value: 'DE', label: t('德國 (DE)') },
                          { value: 'FR', label: t('法國 (FR)') },
                          { value: 'IT', label: t('意大利 (IT)') },
                          { value: 'AU', label: t('澳洲 (AU)') },
                          { value: 'NZ', label: t('紐西蘭 (NZ)') },
                          { value: 'SG', label: t('新加坡 (SG)') },
                          { value: 'MY', label: t('馬來西亞 (MY)') },
                          { value: 'TH', label: t('泰國 (TH)') },
                          { value: 'VN', label: t('越南 (VN)') },
                          { value: 'ID', label: t('印尼 (ID)') },
                          { value: 'PH', label: t('菲律賓 (PH)') },
                          { value: 'IN', label: t('印度 (IN)') },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="declaredValue"
                      label={t('申報價值')}
                      extra={t('用於海關申報的單位價值')}
                      tooltip={t('海關申報價值，通常為商品售價')}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        min={0}
                        precision={2}
                        placeholder={t('申報價值')}
                        disabled={isReadOnly}
                        addonAfter="HKD"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider orientation="left">{t('店鋪設定')}</Divider>
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      name="storeCodes"
                      label={t('Store Code')}
                      extra={t('選擇此產品適用的 Store Code（可多選） — 必須填寫 Store Code 或下方的 Customer 至少其中之一')}
                      dependencies={['customerIds']}
                      rules={[
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const others = getFieldValue('customerIds') as string[] | undefined;
                            const hasStore = Array.isArray(value) && value.length > 0;
                            const hasCustomer = Array.isArray(others) && others.length > 0;
                            if (!hasStore && !hasCustomer) {
                              return Promise.reject(new Error(t('請至少選擇一個 Store Code 或 Customer')));
                            }
                            return Promise.resolve();
                          }
                        })
                      ]}
                    >
                      <Select
                        mode="multiple"
                        style={{ width: '100%' }}
                        placeholder={t('選擇 Store Code')}
                        showSearch
                        filterOption={(input, option) =>
                          (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                        }
                        options={availableStoreCodes.map(code => ({
                          value: code,
                          label: code
                        }))}
                        maxTagCount="responsive"
                        disabled={isReadOnly}
                        onChange={() => form.validateFields(['customerIds']).catch(() => {})}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name="customerIds"
                      label={t('Customer (非 TVMALL)')}
                      extra={t('選擇此產品屬於哪些 Customer（針對非 TVMALL 產品，可多選） — 必須填寫 Store Code 或 Customer 至少其中之一')}
                      dependencies={['storeCodes']}
                      rules={[
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const others = getFieldValue('storeCodes') as string[] | undefined;
                            const hasCustomer = Array.isArray(value) && value.length > 0;
                            const hasStore = Array.isArray(others) && others.length > 0;
                            if (!hasCustomer && !hasStore) {
                              return Promise.reject(new Error(t('請至少選擇一個 Store Code 或 Customer')));
                            }
                            return Promise.resolve();
                          }
                        })
                      ]}
                    >
                      <Select
                        mode="multiple"
                        style={{ width: '100%' }}
                        placeholder={t('選擇 Customer')}
                        showSearch
                        allowClear
                        filterOption={(input, option) =>
                          (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                        }
                        options={availableCustomers.map(customer => ({
                          value: customer.id,
                          label: customer.name
                        }))}
                        maxTagCount="responsive"
                        disabled={isReadOnly}
                        onChange={() => form.validateFields(['storeCodes']).catch(() => {})}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider orientation="left">{t('產品屬性標籤')}</Divider>
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      name="productAttributes"
                      label={t('產品屬性標籤')}
                      extra={t('用於定義產品變體，如顏色、尺寸、規格等。可添加多個標籤，按Enter鍵確認。')}
                    >
                      <Select
                        mode="tags"
                        style={{ width: '100%' }}
                        placeholder={t('輸入產品屬性標籤，例如：顏色、尺寸、規格...')}
                        tokenSeparators={[',']}
                        maxTagCount={10}
                        maxTagTextLength={20}
                        options={attributeOptions}
                        showSearch
                        filterOption={(input, option) =>
                          (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                        }
                        onChange={handleAttributesChange}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Alert
                  message={t('產品屬性標籤說明')}
                  description={
                    <div>
                      <div>• <strong>{t('產品變體')}</strong>{t('：同一 SKU ID 下不同規格或型號的產品')}</div>
                      <div>• <strong>{t('使用場景')}</strong>{t('：服裝尺寸、電子產品容量、食品口味等')}</div>
                      <div>• <strong>{t('示例')}</strong>{t('："幼犬"和"成犬"狗糧、"64GB"和"128GB"手機、"紅色"和"藍色"T恤')}</div>
                      <div>• <strong>{t('優勢')}</strong>{t('：統一管理同類產品，方便庫存和銷售追蹤')}</div>
                    </div>
                  }
                  type="info"
                  showIcon
                  style={{ marginTop: 16 }}
                />
              </TabPane>

              <TabPane tab={t('尺寸重量和單位轉換')} key="dimensions-units">
                {/* 是否包裝開關（默認開啟） */}
                <Row gutter={16} style={{ marginBottom: 8 }}>
                  <Col span={12}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>{t('是否包裝')}</span>
                      <Form.Item name="isPackaged" valuePropName="checked" noStyle>
                        <Switch
                          checked={!!form.getFieldValue('isPackaged')}
                          onChange={(checked) => {
                            try { form.setFieldsValue({ isPackaged: !!checked }); } catch {}
                            if (!checked) syncPackagingFromProduct();
                          }}
                        />
                      </Form.Item>
                    </div>
                  </Col>
                </Row>
                {/* 產品本身尺寸和重量部分 */}
                <Divider orientation="left">{t('產品本身尺寸和重量')}</Divider>
                <Row gutter={16}>
                  <Col span={6}>
                    <Form.Item
                      name="length"
                      label={t('產品長度 (cm)')}
                    >
                      <InputNumber 
                        min={0} 
                        style={{ width: '100%' }} 
                        placeholder={t('產品長度')}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name="width"
                      label={t('產品寬度 (cm)')}
                    >
                      <InputNumber 
                        min={0} 
                        style={{ width: '100%' }} 
                        placeholder={t('產品寬度')}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name="height"
                      label={t('產品高度 (cm)')}
                    >
                      <InputNumber 
                        min={0} 
                        style={{ width: '100%' }} 
                        placeholder={t('產品高度')}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name="weight"
                      label={t('產品重量 (kg)')}
                    >
                      <InputNumber min={0} style={{ width: '100%' }} placeholder={t('產品重量')} />
                    </Form.Item>
                  </Col>
                </Row>

                {/* 包裝尺寸和重量部分（用於CBM計算）*/}
                <Divider orientation="left">{t('包裝/散件尺寸和重量（用於CBM計算）')}</Divider>
                <Row gutter={16}>
                  <Col span={6}>
                    <Form.Item
                      name="packagingLength"
                      label={t('包裝/散件長度 (cm)')}
                    >
                      <InputNumber 
                        min={0} 
                        style={{ width: '100%' }} 
                        placeholder={t('包裝/散件長度')}
                        onChange={() => calculateCBM()}
                        disabled={!form.getFieldValue('isPackaged')}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name="packagingWidth"
                      label={t('包裝/散件寬度 (cm)')}
                    >
                      <InputNumber 
                        min={0} 
                        style={{ width: '100%' }} 
                        placeholder={t('包裝/散件寬度')}
                        onChange={() => calculateCBM()}
                        disabled={!form.getFieldValue('isPackaged')}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name="packagingHeight"
                      label={t('包裝/散件高度 (cm)')}
                    >
                      <InputNumber 
                        min={0} 
                        style={{ width: '100%' }} 
                        placeholder={t('包裝/散件高度')}
                        onChange={() => calculateCBM()}
                        disabled={!form.getFieldValue('isPackaged')}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name="packagingWeight"
                      label={t('包裝/散件重量 (kg)')}
                    >
                      <InputNumber min={0} style={{ width: '100%' }} placeholder={t('包裝重量')} disabled={!form.getFieldValue('isPackaged')} />
                    </Form.Item>
                  </Col>
                </Row>

                {/* CBM計算結果顯示 */}
                <Divider orientation="left">{t('CBM計算結果')}</Divider>
                <Row gutter={16}>
                  <Col span={6}>
                    <Form.Item
                      name="packageCBM"
                      label={t('包裝/散件體積 (m³)')}
                    >
                      <InputNumber min={0} style={{ width: '100%' }} disabled precision={6} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name="unitCBM"
                      label={t('單位體積 (m³)')}
                    >
                      <InputNumber min={0} style={{ width: '100%' }} disabled precision={6} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name="temperatureRequirement"
                      label={t('溫度要求')}
                    >
                      <Select placeholder={t('選擇溫度要求')} allowClear>
                        <Option value="room_temperature">{t('常溫 (15-25°C)')}</Option>
                        <Option value="cold">{t('冷藏 (2-8°C)')}</Option>
                        <Option value="frozen">{t('冷凍 (-18°C以下)')}</Option>
                        <Option value="controlled_room_temp">{t('控制室溫 (20-25°C)')}</Option>
                        <Option value="cool">{t('陰涼 (8-15°C)')}</Option>
                        <Option value="ultra_cold">{t('超低溫 (-80°C以下)')}</Option>
                        <Option value="dry">{t('乾燥 (RH<60%)')}</Option>
                        <Option value="humid_controlled">{t('濕度控制 (45-65% RH)')}</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                {/* 單位和轉換部分 */}
                <Divider orientation="left">{t('單位和轉換設定')}</Divider>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      name="baseUOM"
                      label={t('基本單位')}
                      rules={[{ required: true, message: t('請選擇基本單位') }]}
                    >
                      <Select placeholder={t('選擇基本單位')}>
                        <Option value="PCS">{t('PCS (件)')}</Option>
                        <Option value="KG">{t('KG (公斤)')}</Option>
                        <Option value="M">{t('M (米)')}</Option>
                        <Option value="L">{t('L (升)')}</Option>
                        <Option value="SET">{t('SET (套)')}</Option>
                        <Option value="UNIT">{t('UNIT (單位)')}</Option>
                        <Option value="PACK">{t('PACK (包)')}</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="packagingUOM"
                      label={t('包裝單位')}
                      rules={[{
                        validator: async (_: any, value: any) => {
                          if (!form.getFieldValue('isPackaged')) return Promise.resolve();
                          if (value === undefined || value === null || value === '') return Promise.reject(new Error(t('請選擇包裝單位')));
                          return Promise.resolve();
                        }
                      }]}
                    >
                      <Select placeholder={t('選擇包裝單位')} disabled={!form.getFieldValue('isPackaged')}>
                        <Option value="BOX">{t('BOX (箱)')}</Option>
                        <Option value="CARTON">{t('CARTON (紙箱)')}</Option>
                        <Option value="PALLET">{t('PALLET (托盤)')}</Option>
                        <Option value="BAG">{t('BAG (袋)')}</Option>
                        <Option value="BUNDLE">{t('BUNDLE (捆)')}</Option>
                        <Option value="CASE">{t('CASE (盒)')}</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="conversionFactor"
                      label={t('轉換因子')}
                      rules={[{
                        validator: async (_: any, value: any) => {
                          if (!form.getFieldValue('isPackaged')) return Promise.resolve();
                          if (value === undefined || value === null || Number(value) < 1) return Promise.reject(new Error(t('請輸入轉換因子')));
                          return Promise.resolve();
                        }
                      }]}
                      extra={t('每個包裝包含多少個基本單位')}
                    >
                      <InputNumber 
                        min={1} 
                        style={{ width: '100%' }} 
                        placeholder={t('例如：24（表示1包裝=24個基本單位）')}
                        onChange={() => calculateCBM()}
                        addonAfter={t('個基本單位')}
                        disabled={!form.getFieldValue('isPackaged')}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {/* 轉換顯示 */}
                <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => 
                  prevValues.conversionFactor !== currentValues.conversionFactor ||
                  prevValues.baseUOM !== currentValues.baseUOM ||
                  prevValues.packagingUOM !== currentValues.packagingUOM ||
                  prevValues.isPackaged !== currentValues.isPackaged
                }>
                  {({ getFieldValue }) => {
                    const conversionFactor = getFieldValue('conversionFactor');
                    const baseUOM = getFieldValue('baseUOM');
                    const packagingUOM = getFieldValue('packagingUOM');
                    const isPackaged = !!getFieldValue('isPackaged');
                    
                    if (isPackaged && conversionFactor && baseUOM && packagingUOM) {
                      return (
                        <Alert
                          message={t('包裝轉換關係')}
                          description={
                            <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                              <span style={{ color: '#52c41a' }}>{t('1個包裝')}</span>
                              <span style={{ margin: '0 8px', color: '#1890ff' }}>=</span>
                              <span style={{ color: '#fa8c16' }}>{t('{{count}}個基本單位', { count: conversionFactor })}</span>
                              <br />
                              <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal' }}>
                                {t('即：1')} {packagingUOM} = {conversionFactor} {baseUOM}
                              </span>
                            </div>
                          }
                          type="success"
                          showIcon
                          style={{ marginBottom: 16, backgroundColor: '#f6ffed' }}
                        />
                      );
                    }
                    return null;
                  }}
                </Form.Item>

                <Alert
                  message={t('說明')}
                  description={
                    <div style={{lineHeight: '1.6'}}>

                      <div>• <strong>{t('產品尺寸')}</strong>{t('：記錄產品本身的實際尺寸和重量')}</div>
                      <div>• <strong>{t('轉換因子')}</strong>{t('：定義1個包裝包含多少個基本單位，例如：1箱=24件')}</div>
                      <div>• <strong>{t('包裝體積 (CBM)')}</strong>{t('：根據包裝尺寸自動計算，用於倉儲和物流計費')}</div>
                      <div>• <strong>{t('單位體積 (CBM)')}</strong>{t('：包裝體積÷轉換因子，表示每個基本單位的體積')}</div>
                      <div>• <strong>{t('溫度要求')}</strong>{t('：產品存儲和運輸的溫濕度要求，影響倉庫分區和物流選擇')}</div>
                      <div>• <strong>{t('包裝尺寸')}</strong>{t('：用於CBM計算的外包裝尺寸（包裝長×包裝寬×包裝高÷1,000,000 m³）')}</div>
                      <div>• <strong>{t('包裝/非包裝模式')}</strong>{t('：當「是否包裝」關閉（非包裝/散件）時，包裝尺寸與重量自動等同於產品尺寸與重量，且包裝單位=基本單位、轉換因子=1；相關欄位被禁用且不作為必填。')}
                                                </div>
                    </div>
                  }
                  type="info"
                  showIcon
                />
              </TabPane>

              <TabPane tab={t('庫存管理')} key="inventory">
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      name="currentStock"
                      label={t('當前庫存')}
                      rules={editingProduct ? [] : [{ required: true, message: t('請輸入當前庫存') }]}
                      tooltip={editingProduct ? t('庫存數量應透過庫存移動(Stock Movement)調整，此處僅顯示當前值') : undefined}
                    >
                      <InputNumber min={0} style={{ width: '100%' }} placeholder={t('當前庫存')} disabled={!!editingProduct} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="reorderLevel"
                      label={t('重新訂購水平')}
                    >
                      <InputNumber min={0} style={{ width: '100%' }} placeholder={t('重新訂購水平')} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="safetyStock"
                      label={t('安全庫存')}
                    >
                      <InputNumber min={0} style={{ width: '100%' }} placeholder={t('安全庫存')} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="maxStock"
                      label={t('最大庫存')}
                      rules={[{ required: true, message: t('請輸入最大庫存') }]}
                    >
                      <InputNumber min={0} style={{ width: '100%' }} placeholder={t('最大庫存')} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="sellingPrice"
                      label={t('銷售價格')}
                      extra={t('設定產品的銷售價格，用於計算套裝總價值')}
                    >
                      <InputNumber 
                        min={0} 
                        style={{ width: '100%' }} 
                        placeholder={t('銷售價格')} 
                        precision={2}
                        addonBefore="$"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Alert
                  message={t('重新訂購水平警報')}
                  description={t('當庫存等於或低於重新訂購水平時，系統將發送警報通知。')}
                  type="warning"
                  showIcon
                />
                <Divider style={{ margin: '24px 0' }}>{t('銷售渠道配比')}</Divider>
                <Card size="small" style={{ marginBottom: 16 }}>
                  <Form.List name="salesChannelRatios">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...restField }) => (
                          <div key={key} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '12px', 
                            marginBottom: '12px',
                            padding: '8px',
                            border: '1px solid #f0f0f0',
                            borderRadius: '6px',
                            backgroundColor: '#fafafa'
                          }}>
                            <div style={{ flex: '0 0 200px' }}>
                              <Form.Item
                                {...restField}
                                name={[name, 'channel']}
                                rules={[{ required: true, message: t('請選擇渠道') }]}
                                style={{ marginBottom: 0 }}
                              >
                                <Select placeholder={t('選擇渠道')}>
                                  {salesChannelOptions.map((c) => (
                                    <Option key={c} value={c}>{c}</Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            </div>
                            <div style={{ flex: '0 0 120px' }}>
                              <Form.Item
                                {...restField}
                                name={[name, 'ratio']}
                                rules={[{ required: true, message: t('請輸入比例(%)') }]}
                                style={{ marginBottom: 0 }}
                              >
                                <InputNumber 
                                  addonAfter="%" 
                                  min={0} 
                                  max={100} 
                                  style={{ width: '100%' }}
                                  onChange={() => {
                                    setTimeout(() => form.validateFields(['salesChannelRatios']), 0);
                                  }}
                                />
                              </Form.Item>
                            </div>
                            <div style={{ flex: '0 0 100px' }}>
                              <Form.Item shouldUpdate={(prevValues, currentValues) => {
                                const prevRatio = prevValues.salesChannelRatios?.[name]?.ratio;
                                const currentRatio = currentValues.salesChannelRatios?.[name]?.ratio;
                                const prevStock = prevValues.currentStock;
                                const currentStock = currentValues.currentStock;
                                return prevRatio !== currentRatio || prevStock !== currentStock;
                              }} style={{ marginBottom: 0 }}>
                                {({ getFieldValue }) => {
                                  const ratio = getFieldValue(['salesChannelRatios', name, 'ratio']) || 0;
                                  const currentStock = getFieldValue('currentStock') || 0;
                                  const calculatedQty = Math.floor((currentStock * ratio) / 100);
                                  return (
                                    <div style={{ 
                                      padding: '6px 12px', 
                                      background: '#f5f5f5', 
                                      borderRadius: '4px',
                                      textAlign: 'center',
                                      fontSize: '14px',
                                      color: '#666',
                                      height: '32px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      border: '1px solid #d9d9d9'
                                    }}>
                                      {calculatedQty} {t('件')}
                                                                              </div>
                                  );
                                }}
                              </Form.Item>
                            </div>
                            <div style={{ flex: '0 0 60px' }}>
                              <Button 
                                danger 
                                size="small"
                                onClick={() => remove(name)}
                                icon={<DeleteOutlined />}
                                style={{ height: '32px', width: '100%' }}
                              >
                                {t('刪除')}
                                                                      </Button>
                            </div>
                          </div>
                        ))}
                        <Button 
                          type="dashed" 
                          onClick={() => add({ ratio: 0 })} 
                          block
                          style={{ marginTop: '8px' }}
                        >
                          {t('新增渠道')}
                                                                              </Button>
                      </>
                    )}
                  </Form.List>
                  <Alert
                    style={{ marginTop: 12 }}
                    type="info"
                    showIcon
                    message={t('分配邏輯')}
                    description={t('保存後，系統會將 SKU 可分配庫存依據各渠道百分比切分。比例總和需為 100%。數量 = floor(當前庫存 × 百分比 ÷ 100)')}
                  />
                </Card>
                        </TabPane>

          <TabPane tab={t('批次控制設定')} key="batchControl">
            <Alert
              message={t('批次控制策略說明')}
              description={t('選擇適合您業務需求的批次控制策略，系統將根據所選策略自動管理庫存出入庫順序。')}
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Row gutter={16}>
              <Col span={16}>
                <Form.Item
                  name="batchControlType"
                  label={t('預設訂單分配策略')}
                  rules={[{ required: true, message: t('請選擇批次控制策略') }]}
                  style={{ marginBottom: 24 }}
                >
                  <Select 
                    placeholder={t('請選擇批次控制策略')}
                    size="large"
                    style={{ width: '100%' }}
                  >
                    {(Array.isArray((window as any).__dynamicBatchConfigs) && (window as any).__dynamicBatchConfigs.length
                      ? (window as any).__dynamicBatchConfigs
                      : batchControlConfigs).map((config: any) => (
                      <Option key={config.type} value={config.type}>
                        {config.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label=" " colon={false}>
                  <Button 
                    type="default"
                    icon={<EyeOutlined />}
                    onClick={() => {
                      const currentType = form.getFieldValue('batchControlType');
                      const cfgs: any[] = (Array.isArray((window as any).__dynamicBatchConfigs) && (window as any).__dynamicBatchConfigs.length
                        ? (window as any).__dynamicBatchConfigs
                        : batchControlConfigs);
                      const selectedConfig = cfgs.find(c => c.type === currentType);
                      if (selectedConfig) {
                        showBatchControlDemo(selectedConfig);
                      } else {
                        message.warning(t('請先選擇一個批次控制策略'));
                      }
                    }}
                    disabled={!form.getFieldValue('batchControlType')}
                  >
                    {t('查看演示')}
                                                                    </Button>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.batchControlType !== currentValues.batchControlType}>
              {({ getFieldValue }) => {
                const selectedType = getFieldValue('batchControlType');
                const configs3: any[] = (Array.isArray((window as any).__dynamicBatchConfigs) && (window as any).__dynamicBatchConfigs.length
                  ? (window as any).__dynamicBatchConfigs
                  : batchControlConfigs);
                const selectedConfig = configs3.find(c => c.type === selectedType);
                
                if (!selectedConfig) return null;
                
                return (
                  <Card 
                    size="small" 
                    style={{ 
                      marginBottom: 24, 
                      backgroundColor: '#f6ffed',
                      border: '1px solid #b7eb8f' 
                    }}
                  >
                    <Row>
                      <Col span={18}>
                        <Title level={5} style={{ marginBottom: 8, color: '#52c41a' }}>
                          {selectedConfig.label}
                        </Title>
                        <Paragraph style={{ marginBottom: 12, color: '#595959' }}>
                          <Text strong>{t('策略說明：')}</Text> {selectedConfig.explanation}
                        </Paragraph>
                        <div>
                          <Text strong style={{ color: '#595959' }}>{t('適用場景：')}</Text>
                          <div style={{ marginTop: 4 }}>
                            {selectedConfig.useCases.map((useCase: string, index: number) => (
                              <Tag key={index} color="green" style={{ marginBottom: 4 }}>
                                {useCase}
                              </Tag>
                            ))}
                          </div>
                        </div>
                      </Col>
                      <Col span={6} style={{ textAlign: 'right' }}>
                        <Button 
                          type="primary"
                          ghost
                          size="small"
                          onClick={() => showBatchControlDemo(selectedConfig)}
                          style={{ marginTop: 8 }}
                        >
                          {t('查看排序演示')}
                                                            </Button>
                      </Col>
                    </Row>
                  </Card>
                );
              }}
            </Form.Item>

            <Divider style={{ marginTop: 32, marginBottom: 24 }}>{t('批次管理設置')}</Divider>

            <Card size="small" style={{ backgroundColor: '#fafafa', marginBottom: 24 }}>
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    name="lotNumberRequired"
                    label={t('批次號管理')}
                    valuePropName="checked"
                    extra={t('是否要求該產品必須有批次號進行追蹤')}
                    style={{ marginBottom: 0 }}
                  >
                    <Switch
                      checkedChildren={t('必需')}
                      unCheckedChildren={t('可選')}
                      size="default"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="serialNumberRequired"
                    label={t('序列號管理')}
                    valuePropName="checked"
                    extra={t('是否要求該產品必須有序列號進行追蹤')}
                    style={{ marginBottom: 0 }}
                  >
                    <Switch
                      checkedChildren={t('必需')}
                      unCheckedChildren={t('可選')}
                      size="default"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Alert
              message={t('重要說明')}
              description={
                <div style={{ lineHeight: '1.6' }}>
                  <div>{t('• 批次控制策略將影響該產品的庫存分配和出庫順序')}</div>
                  <div>{t('• 入庫和出庫操作時，系統會自動根據所選策略進行排序處理')}</div>
                  <div>{t('• 啟用批次號或序列號管理後，入庫和出庫都需要提供相應追蹤信息')}</div>
                  <div>{t('• 變更策略後，新的出庫操作將立即使用新的分配策略')}</div>
                </div>
              }
              type="info"
              showIcon
              style={{ backgroundColor: '#e6f7ff', border: '1px solid #91d5ff' }}
            />
          </TabPane>

          <TabPane tab={t('產品套裝')} key="bundle" forceRender>
            <Alert
              message={t('產品套裝管理')}
              description={t('當啟用產品套裝功能時，此SKU將成為一個包含多個子產品的套裝產品。適用於組合套裝、禮品盒、配件套裝等場景。')}
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />
            
            <Card title={t('套裝設置')} style={{ marginBottom: 24 }}>
              <Form.Item name="isBundle" valuePropName="checked">
                <Space>
                  <Switch
                    checked={!!form.getFieldValue('isBundle')}
                    onChange={(checked) => {
                      setBundleEnabled(!!checked);
                      try { 
                        form.setFieldsValue({ isBundle: !!checked }); 
                        // 當重新開啟套裝功能時，恢復原始的套裝產品數據
                        if (checked && lastDetailRef.current && Array.isArray(lastDetailRef.current.bundleItems)) {
                          form.setFieldsValue({ 
                            bundleItems: lastDetailRef.current.bundleItems.map((it: any) => ({ 
                              productId: it.productId, 
                              quantity: Number(it.quantity || 1), 
                              sku: it.sku, 
                              productName: it.productName 
                            })) 
                          });
                        }
                      } catch {}
                    }}
                  />
                  <div>
                    <Text strong style={{ fontSize: '16px' }}>{t('啟用產品套裝功能')}</Text>
                    <br />
                    <Text type="secondary">{t('開啟後可以添加多個SKU產品組成套裝')}</Text>
                  </div>
                </Space>
              </Form.Item>
              {/* 應你的要求，移除上方快捷添加區塊 */}
            </Card>

            {/* 產品套裝詳細設置 */}
            <Form.Item noStyle>
              {(bundleEnabled || isBundleEnabled) ? (
                  <Card style={{ marginTop: 16, border: 'none', boxShadow: 'none' }}>
                    {/* 套裝配置說明提示已按要求移除 */}
                    
                    <Form.List name="bundleItems">
                      {(fields, { add, remove }) => (
                        <>
                          {/* 行卡片樣式簡化，移除"商品 X/配置…"標題 */}
                          {fields.map(({ key, name, ...restField }, index) => (
                            <div key={key} style={{ padding: 12, marginBottom: 12, background: '#fff', border: '1px solid #e6f7ff', borderRadius: 6 }}>
                              <Row gutter={16} align="middle" wrap={false}>
                                <Col span={10}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'productId']}
                                    label={
                                      <Space>
                                        <ShoppingOutlined />
                                        <Text strong>{t('產品')}</Text>
                                      </Space>
                                    }
                                    rules={[{ required: true, message: t('請選擇一個產品') }]}
                                  >
                                    <Select
                                      placeholder={t('請輸入產品名稱或SKU搜尋（至少2個字元）')}
                                      showSearch
                                      size="large"
                                      filterOption={false}
                                      optionLabelProp="label"
                                      popupMatchSelectWidth={false}
                                      onSearch={handleBundleProductSearch}
                                      loading={bundleSearchLoading}
                                      notFoundContent={bundleSearchLoading ? <span>{t('搜尋中...')}</span> : <span>{t('請輸入至少2個字元搜尋產品')}</span>}
                                      onChange={(productId) => {
                                        // 合併本地快取與搜尋結果
                                        const allSources = [...allProductsCache, ...bundleSearchResults];
                                        const uniqueMap = new Map(allSources.map(p => [p.id, p]));
                                        const selectedProduct = uniqueMap.get(productId);
                                        if (selectedProduct) {
                                          const currentBundleItems = form.getFieldValue('bundleItems') || [];
                                          currentBundleItems[name] = {
                                            ...currentBundleItems[name],
                                            productId: selectedProduct.id,
                                            productName: selectedProduct.productName,
                                            sku: selectedProduct.sku,
                                            baseUOM: selectedProduct.baseUOM,
                                            currentStock: selectedProduct.currentStock,
                                            sellingPrice: selectedProduct.sellingPrice || 0,
                                          };
                                          form.setFieldsValue({ bundleItems: currentBundleItems });
                                        }
                                      }}
                                    >
                                      {(() => {
                                        // 如果有搜尋結果，優先顯示搜尋結果；否則顯示本地快取
                                        const source = bundleSearchResults.length > 0
                                          ? bundleSearchResults
                                          : allProductsCache;

                                        console.log('[Bundle Dropdown] Using source:', bundleSearchResults.length > 0 ? 'searchResults' : 'cache', 'count:', source.length);

                                        const filteredProducts = source.filter(p => {
                                          // Don't include the current product being edited
                                          if (editingProduct && p.id === editingProduct.id) return false;
                                          // Only include active products (treat undefined/null as active)
                                          if (p.isActive === false) return false;
                                          // Exclude bundle products
                                          if (p.isBundle === true) return false;
                                          // Products should have a valid selling price (allow 0 and undefined)
                                          if (p.sellingPrice !== undefined && p.sellingPrice !== null && p.sellingPrice < 0) return false;
                                          return true;
                                        });

                                        console.log('[Bundle Dropdown] Filtered products count:', filteredProducts.length);
                                        const currentBI = form.getFieldValue('bundleItems')?.[name];
                                        const productsToShow = [...filteredProducts];
                                        if (currentBI?.productId && !productsToShow.some((p) => p.id === currentBI.productId)) {
                                          productsToShow.unshift({ id: currentBI.productId, sku: currentBI.sku || '', productName: currentBI.productName || t('(已選產品)') } as any);
                                        }
                                        return productsToShow;
                                      })().map(product => (
                                        <Option
                                          key={product.id}
                                          value={product.id}
                                          label={product.productName || product.sku || product.id}
                                          sku={product.sku}
                                        >
                                          {product.sku} - {product.productName}
                                        </Option>
                                      ))}
                                    </Select>
                                  </Form.Item>
                                </Col>
                                <Col span={6}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'quantity']}
                                    label={
                                      <Space>
                                        <NumberOutlined />
                                        <Text strong>{t('數量')}</Text>
                                      </Space>
                                    }
                                    rules={[
                                      { required: true, message: t('請輸入數量') },
                                      { type: 'number', min: 1, message: t('數量必須大於0') }
                                    ]}
                                  >
                                    <InputNumber 
                                      min={1} 
                                      placeholder={t('輸入數量')} 
                                      style={{ width: '100%' }}
                                      size="large"
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={5}>
                                  <Form.Item
                                    shouldUpdate={(prev, curr) => {
                                    const prevItem = prev?.bundleItems?.[name];
                                    const currItem = curr?.bundleItems?.[name];
                                      return prevItem?.sku !== currItem?.sku;
                                    }}
                                    label={<Text strong>SKU</Text>}
                                  >
                                    {({ getFieldValue }) => {
                                      const currentItem = getFieldValue('bundleItems')?.[name];
                                      const skuVal = currentItem?.sku || '';
                                      return <Tooltip title={skuVal}><Input disabled value={skuVal} size="large" style={{ width: '100%' }} /></Tooltip>;
                                    }}
                                  </Form.Item>
                                </Col>
                                <Col span={3} style={{ textAlign: 'right' }}>
                                  <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => remove(name)}
                                  />
                                          </Col>
                                        </Row>
                                      </div>
                          ))}
                          
                          <Button
                            type="dashed"
                            onClick={() => add({ quantity: 1 })}
                            block
                            icon={<PlusOutlined />}
                            size="large"
                            style={{ 
                              marginTop: 16, 
                              height: '60px',
                              border: '2px dashed #1890ff',
                              color: '#1890ff'
                            }}
                          >
                            <Text strong style={{ fontSize: '16px' }}>{t('添加套裝商品')}</Text>
                          </Button>
                          {/* 套裝總覽區塊已移除 */}
                        </>
                      )}
                    </Form.List>
                  </Card>
                ) : null
              }
            </Form.Item>
          </TabPane>

          <TabPane tab={t('高級設置')} key="advanced" forceRender>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="isActive" valuePropName="checked">
                      <Switch /> {t('活躍狀態')}
                                                                  </Form.Item>
                  </Col>
                </Row>

                <Alert
                  message={t('其他高級設置')}
                  description={t('更多高級配置選項將在未來版本中提供')}
                  type="info"
                  showIcon
                  style={{ marginTop: 24 }}
                />

                {/* 產品套裝功能已移至專門的"產品套裝"標籤頁 */}



                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="alertEnabled" valuePropName="checked">
                      <Switch /> {t('啟用警報')}
                                                                  </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="multiStoreEnabled" valuePropName="checked">
                      <Switch /> {t('啟用多倉儲')}
                                                                  </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  label={t('警報郵件地址')}
                  name="alertEmails"
                >
                  <Select
                    mode="tags"
                    placeholder={t('輸入郵件地址')}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </TabPane>
            </Tabs>
            </Form>
          </>
        )}
      </Modal>

      {/* 批量上傳產品Modal */}
      <ProductBulkUploadModal
        visible={bulkUploadVisible}
        onCancel={() => setBulkUploadVisible(false)}
        onUploadComplete={handleBulkUploadComplete}
      />
    </div>
  );
};

export default ProductMasterSetup; 